import { deriveAnchoredAge } from '../utils/levelCalculations';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
  ComposedChart, Scatter, ZAxis, Rectangle
} from 'recharts';
import { FaChartBar, FaEdit, FaUser, FaList, FaCalculator } from 'react-icons/fa';
import { DIMENSION_STYLES, UnifiedHeader, UnifiedLegend } from '../styles/DimensionStyles';
import { calculateExpectedPercentage, calculatePreviousYearPercentage } from '../utils/levelCalculations';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';

// Definimos las interfaces localmente
interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean;
}

interface DimensionSummaryProps {
  student: Student;
  dimensionId: number;
}

const COLORS = {
  achieved: '#4A90E2',
  expected: '#F5A623',
  previousYear: '#6B7280'
};

const LEVEL_COLORS = {
  expected: 'bg-yellow-50 border-2 border-yellow-400',    // Color amarillo claro para nivel esperado
  previous: 'bg-gray-50 border-2 border-gray-400',        // Color gris claro para nivel del año anterior
  selected: 'bg-blue-500 text-white shadow-md',           // Color azul para nivel seleccionado
  default: 'bg-white hover:bg-blue-50 border border-gray-200', // Color por defecto
  notApplicable: 'bg-gray-50 text-gray-400 italic',      // Color para "No aplica" no seleccionado
  notApplicableSelected: 'bg-blue-500 text-white shadow-md italic', // Color para "No aplica" seleccionado
  previouslySelected: 'bg-blue-50 border border-blue-300', // Color para nivel previamente seleccionado
};

const CustomBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  return <Rectangle x={x} y={y} width={width} height={height} fill={fill} />;
};

const CustomVerticalRectangle = (props: any) => {
  const { cx, cy, fill } = props;
  return (
    <Rectangle
      x={cx - 1}
      y={cy - 25}
      width={4}
      height={40}
      fill={fill}
    />
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow">
        <p className="label">{`${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={`item-${entry.name}-${index}`} style={{ color: entry.fill }}>
            {`${entry.name}: ${entry.value ? entry.value.toFixed(2) : 'N/A'}%`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Definición de la dimensión y habilidades
export const pensamientoMatematicosDimension: Dimension =   {
  id: 9,
  name: "Pensamiento matemático",
  "skills": [
    {
      "id": 1,
      "name": "i1. OA2 Identifica atributos de los objetos como forma y color.",
      "levels": [
        "i1. OA2 Identifica atributos de los objetos como forma y color.",
        "i1.OA2 Describe atributos de objetos, tales como forma, color, tamaño, función, masa o materialidad, entre otros.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 2,
      "name": "i2.OA2 Describe algunas semejanzas y diferencias al comparar objetos, considerando dos atributos a la vez.",
      "levels": [
        "No Aplica",
        "i2.OA2 Describe algunas semejanzas y diferencias al comparar objetos, considerando dos atributos a la vez.",
        "i1.OA2 Describe algunas semejanzas y diferencias al comparar objetos, considerando tres atributos a la vez.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 3,
      "name": "i2.OA2 Agrupa elementos por dos atributos que tienen en común como forma y color usando material concreto.",
      "levels": [
        "i2.OA2 Agrupa elementos por dos atributos que tienen en común como forma y color usando material concreto.",
        "i3.OA2 Agrupa elementos por dos atributos que tienen en común (como forma, color, tamaño, función, masa o materialidad, entre otros), usando material concreto o pictórico.",
        "i2.OA2 Agrupa elementos por tres atributos que tienen en común (como forma, color, tamaño, función, masa o materialidad, entre otros), usando material concreto y pictórico.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 4,
      "name": "i4.OA2 Nombra los atributos que usó al clasificar elementos.",
      "levels": [
        "No Aplica",
        "i4.OA2 Nombra los atributos que usó al clasificar elementos.",
        "i3.OA2 Nombra los criterios que usó al clasificar elementos, por tres atributos a la vez, usando material concreto y pictórico.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 5,
      "name": "i.3 OA2 Ordena una serie, por ensayo y error, según altura o longitud utilizando material concreto.",
      "levels": [
        "i.3 OA2 Ordena una serie, por ensayo y error, según altura o longitud utilizando material concreto.",
        "i5.OA2 Ordena una serie, por ensayo y error, según longitud o capacidad para contener, usando material concreto o pictórico.",
        "i4.OA2 Ordena una serie sistemática, según longitud o capacidad para contener, usando material concreto y pictórico.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 6,
      "name": "i5.OA2 Incluye elementos dentro de una serie ordenada según longitud o capacidad para contener.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "i5.OA2 Incluye elementos dentro de una serie ordenada según longitud o capacidad para contener.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 7,
      "name": "i4.OA1 Señala (indica o nombra) el patrón en una serie de movimientos, gestos, sonidos usando material concreto .",
      "levels": [
        "i4.OA1 Señala (indica o nombra) el patrón en una serie de movimientos, gestos, sonidos usando material concreto .",
        "i6.OA1 Señala (indica o nombra) el patrón en una serie de movimientos, gestos, sonidos, con material concreto o pictórico.",
        "i6.OA1 Señala (indica o nombra) el patrón en una serie de movimientos, gestos, sonidos, usando material concreto, pictórico y simbólico.",
        "i1.OA11 Describe y reproduce patrones repetitivos que tienen de 1 a 4 elementos, utilizando material concreto, representaciones pictóricas y de manera simbólica.",
        "i1.OA12 Idetifican números que se repiten en secuencias numéricas y explican mediante ejemplos, la regla usada para un patrón numérico dado.",
        "OA12 Describen la regla de un patrón repetitivo identificando el punto de partida e indica cómo sigue el patrón.",
        "OA13 Descubre un error en una secuencia o una tabla y lo corrigen.",
        "OA14Descubren una regla para un patrón en una sucesión y completa los elementos que siguen en ella. Registra los resultados en una tabla.",
        "OA9Establecen relaciones que se dan entre los valores dados en una tabla, usando lenguaje matemático"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 8,
      "name": "i2.OA12 Identifica patrones numéricos en la tabla del 100, la recta numérica y el calendario.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "i2.OA12 Identifica patrones numéricos en la tabla del 100, la recta numérica y el calendario.",
        "OA12 Ubican y explican varios patrones de crecimiento ascendente y descendentes en una tabla de 100 de manera horizontal, vertical y diagonal.",
        "OA13 Analizan patrones en tablas, identificando errores, elementos faltantes y lo corrigen.",
        "OA14 Escribe reglas para patrones dados en sucesiones y determinan cualquier elemento de la secuencia, usando la regla.",
        "oA9 Formulan una regla que se da entre los valores de dos columnas de números de una tabla de valores."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 9,
      "name": "i5.OA1 Extiende patrones de movimientos, gestos, sonidos, con material concreto de dos elementos.",
      "levels": [
        "i5.OA1 Extiende patrones de movimientos, gestos, sonidos, con material concreto de dos elementos.",
        "i7.OA1 Extiende patrones de movimientos, gestos, sonidos, de material concreto o pictórico, con tres elementos.",
        "i7.OA1 Extiende patrones de movimientos, gestos, sonidos, con material concreto, pictórico y simbólico de tres elementos.",
        "i2.OA11 Extiende patrones de manera simbólica de hasta 4 elementos",
        "No Aplica",
        "OA12 Identifican la regla de un patrón de crecimeinto ascendente y descente y extiende los 4 pasos siguientes del patrón",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 10,
      "name": "i6.OA1 Anticipa el elemento que continúa en un patrón, incluyendo el movimiento, gesto, sonido, con material concreto que falta en una serie de dos elementos.",
      "levels": [
        "i6.OA1 Anticipa el elemento que continúa en un patrón, incluyendo el movimiento, gesto, sonido, con material concreto que falta en una serie de dos elementos.",
        "i8.OA1 Anticipa el elemento que continúa en un patrón, incluyendo el movimiento, gesto, sonido, con material concreto o pictórico que falta en una serie de tres elementos.",
        "i8.OA1 Anticipa el elemento que continúa en un patrón, incluyendo el movimiento, gesto, sonido, con material concreto, pictórico y simbólico que falta en una serie de tres elementos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 11,
      "name": "i9.OA1 Elimina el elemento que no corresponde en un patrón de movimientos, gestos, sonidos, con material concreto o pictórico, de dos o tres elementos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "i9.OA1 Elimina el elemento que no corresponde en un patrón de movimientos, gestos, sonidos, con material concreto o pictórico, de dos o tres elementos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 12,
      "name": "i9.OA1 Crea un patrón de dos o más elementos, usando movimientos corporales, gestos, sonidos y material concreto.",
      "levels": [
        "No Aplica",
        "i9.OA1 Crea un patrón de dos o más elementos, usando movimientos corporales, gestos, sonidos y material concreto.",
        "i10.OA1 Crea un patrón de tres elementos o más, usando movimientos corporales, gestos, sonidos, material concreto, pictórico y simbólico.",
        "i3.OA11 Identifica los elementos que faltan en un patrón repetitivo.",
        "i3.OA12 Determina en patrones crecientes el número que falta en una situación pictórica y simbólica, fundamentando la solución.",
        "OA12 Identifican y decriben la regla y completan partes faltantes de un patrón de crecimineto ascendente/ descendente dado en situaciones concretas, pictóricas y simbólicas.",
        "OA13Determinan elementos faltantes en una lista o tabla.",
        "No Aplica",
        "OA9Usando la relación enre los valores de una tabla, predicen los valores de un término desconocido y verifican la predicción."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 13,
      "name": "i11.OA1 Transfiere un patrón de material concreto a uno pictórico, de dos o tres elementos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "i11.OA1 Transfiere un patrón de material concreto a uno pictórico, de dos o tres elementos.",
        "No Aplica",
        "i4.OA12 Crea un patrón numérico, usando una regla y la explican (en el ámbito del 0 al 100).",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA9 Comprenden la relación entre los valores de una tabla, identificando patrones y formulando una regla."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 14,
      "name": "OA12 Solucionan un problema, utilizando patrones de crecimiento ascendentes/descendentes",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA12 Solucionan un problema, utilizando patrones de crecimiento ascendentes/descendentes",
        "No Aplica",
        "No Aplica",
        "OA9 Crean una tabla de valores para registrar información y destacar un patrón cuando se resuelve un problema."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 15,
      "name": "OA12 Determinan igualdades o desigualdades entre cantidades, usando una balanza.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA12 Determinan igualdades o desigualdades entre cantidades, usando una balanza.",
        "OA13 Determinan y registran dos igualdades, con el uso de una balanza para verificar su resultado.",
        "OA 13 Resuelven una ecuación, aplicando estrategias como: Ensayo y error, \"Utilizar la operación inversa\", en forma concreta, simbólica y pictórica.",
        "OA14 Modelan ecuaciones con una balanza, real o pictóricamente; por ejemplo: x+2=4",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 16,
      "name": "OA12 Explican igualdades o desigualdades, usando una balanza",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA12 Explican igualdades o desigualdades, usando una balanza",
        "OA13 Determinan y registran dos desigualdades dadas, con el uso de una balanza para verificar su resultado.",
        "No Aplica",
        "OA14 Modelan inecuaciones con una balanza real o pictóricamente, que se encuentra desequilibrio, por ejemplo: 2+x<7",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 17,
      "name": "OA12 Ordenan cantidades, empleando una balanza.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA12 Ordenan cantidades, empleando una balanza.",
        "OA13 Comparan y registran igualdades o desigualdades con el uso de símbolos (>, <, =) en forma pictórica y simbólica.",
        "No Aplica",
        "OA14 Resuelven ecuaciones e inecuaciones de un paso que involucran adiciones y sustracciones. Por ejemplo: 2+___=7",
        "OA15 Resuelven una ecuación simple de primer grado con una incognita que involucre adiciones y sustracciones.",
        "OA11 Resolven ecuaciones de primer grado con una incógnita, utilizando distintas estrategias."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 18,
      "name": "OA12 Resuelven problemas que involucran igualdades y/o desigualdades, usando una balanza.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA12 Resuelven problemas que involucran igualdades y/o desigualdades, usando una balanza.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA15 Resuelven problemas, usando ecuaciones de un paso que involucren adiciones y sustracciones, en forma pictórica y simbólica.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 19,
      "name": "OA10 Evaluan expresiones algebraicas cuando n es un número natural y analizan generalizaciones.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA10 Evaluan expresiones algebraicas cuando n es un número natural y analizan generalizaciones."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 20,
      "name": "i7.OA3 Señala (indica o nombra) la posición de objetos y personas respecto de sí mismo, utilizando un concepto de ubicación (dentro/fuera; encima/debajo) y distancia (cerca/lejos) en situaciones cotidianas y lúdicas.",
      "levels": [
        "i7.OA3 Señala (indica o nombra) la posición de objetos y personas respecto de sí mismo, utilizando un concepto de ubicación (dentro/fuera; encima/debajo) y distancia (cerca/lejos) en situaciones cotidianas y lúdicas.",
        "i10.OA3 Señala (indica o nombra) la posición de objetos y personas respecto de sí mismo, utilizando un concepto de ubicación (dentro/fuera; encima/debajo/entre; al frente de/detrás de), distancia (cerca/lejos) y dirección (adelante/atrás/hacia el lado), en situaciones cotidianas y lúdicas.",
        "i12.OA3 Señala (indica o nombra) la posición de objetos y personas respecto de un punto de referencia (distinto de sí mismo), en situaciones cotidianas y lúdicas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 21,
      "name": "i11.OA3 Utiliza al menos un concepto de posición, distancia o dirección, respecto de sí mismo, al describir, ubicarse o manipular objetos, en situaciones cotidianas y lúdicas.",
      "levels": [
        "No Aplica",
        "i11.OA3 Utiliza al menos un concepto de posición, distancia o dirección, respecto de sí mismo, al describir, ubicarse o manipular objetos, en situaciones cotidianas y lúdicas.",
        "i13.OA3 Utiliza dos o más conceptos de ubicación (dentro/fuera; encima/debajo/entre; al frente de/detrás de), distancia (cerca/lejos) y dirección (adelante/atrás/hacia el lado), al describir la posición de objetos y personas respecto de un punto u objeto de referencia.",
        "OA13 Describen la posición de objetos y personas con relación a sí mismos y a otros (derecha e izquierda)",
        "OA14 Describen y representan la posición de objetos y personas con relación a sí mismo y a otros.",
        "OA14 Describen la localización de un objeto en un mapa simple.",
        "OA15 Describen e identifican posiciones de objetos en mapas o plano reales.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 22,
      "name": "OA15 Describen trayectos en desplazamientos de objetos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA15 Describen trayectos en desplazamientos de objetos.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 23,
      "name": "OA13 Ubican la posición de un objeto, siguiendo dos instrucciones de posición, ubicación y dirección, usando un punto de referencia.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA13 Ubican la posición de un objeto, siguiendo dos instrucciones de posición, ubicación y dirección, usando un punto de referencia.",
        "OA14 Ubican la posición de un objeto siguiendo más de dos instrucciones de posición, ubicación y dirección, usando un punto de referencia.",
        "OA14 Describen la localización de un objeto en una cuadrícula otorgando letras o números a las columnas y filas.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 24,
      "name": "Indentifica dibujando puntos en el primer cuadrante del plano cartesiano, dadas sus coordenadas en números naurales.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Indentifica dibujando puntos en el primer cuadrante del plano cartesiano, dadas sus coordenadas en números naurales.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 25,
      "name": "i8.OA9 Describe lo que ve de un objeto al observarlo desde algunas de sus posiciones, en situaciones concretas y cotidianas",
      "levels": [
        "i8.OA9 Describe lo que ve de un objeto al observarlo desde algunas de sus posiciones, en situaciones concretas y cotidianas",
        "OA9 Describe lo que ve de un objeto al observarlo desde arriba, desde el lado y desde abajo, en situaciones concretas.",
        "OA9 Nombra o señala lo que podría ver de un objeto al observarlo desde arriba, desde el lado y desde abajo, en situaciones concretas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 26,
      "name": "OA9 Dibuja lo que ve de un objeto al observarlo desde arriba, del lado y desde abajo.",
      "levels": [
        "No Aplica",
        "OA9 Dibuja lo que ve de un objeto al observarlo desde arriba, del lado y desde abajo.",
        "OA9 Dibuja un objeto desde arriba, del lado y desde abajo, observado en dibujos, fotografías o TICs, comparando lo que ve.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 27,
      "name": "OA9 Nombra o señala la posición desde la que cree que se observa un objeto (desde arriba, del lado y desde abajo) representado a través de dibujos, fotografías o TICs.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "OA9 Nombra o señala la posición desde la que cree que se observa un objeto (desde arriba, del lado y desde abajo) representado a través de dibujos, fotografías o TICs.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 28,
      "name": "OA10 Señala (indica o nombra) figuras 2D y 3D, que observa en forma directa.",
      "levels": [
        "No Aplica",
        "OA10 Señala (indica o nombra) figuras 2D y 3D, que observa en forma directa.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 29,
      "name": "i9.OA10 Nombra algunos atributos (forma, cantidad de lados, caras) de figuras 3D, que observa en objetos de su entorno.",
      "levels": [
        "i9.OA10 Nombra algunos atributos (forma, cantidad de lados, caras) de figuras 3D, que observa en objetos de su entorno.",
        "OA10 Nombra atributos (forma, cantidad de lados, caras) de figuras 2D y 3D, que observa en forma directa.",
        "OA10 Describe figuras 2D y 3D, considerando sus atributos (forma, cantidad de lados, vértices, caras), al observarlas en forma directa o a través de TIC.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 30,
      "name": "OA14 Muestra diferencias que se dan entre dos figuras 2D",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA14 Muestra diferencias que se dan entre dos figuras 2D",
        "OA15 Describe figuras 2D con sus propias palabras y determinan sus diferencias.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 31,
      "name": "OA14 Clasifica figuras 2D y explican el criterio de clasificación usado",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA14 Clasifica figuras 2D y explican el criterio de clasificación usado",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 32,
      "name": "OA14 Relaciona partes de una figura 3D con partes de figuras 2D",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA14 Relaciona partes de una figura 3D con partes de figuras 2D",
        "OA15 Compara figuras 2D con figuras 3D dado el atributo.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 33,
      "name": "OA15 Construye figuras 2D (triángulo, cuadrado, rectángulo y círculo) con material concreto como tangrama, papel u otros.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA15 Construye figuras 2D (triángulo, cuadrado, rectángulo y círculo) con material concreto como tangrama, papel u otros.",
        "Ver OA 17 Reconocer en el entorno figuras 2D que están trasladadas, reflejadas y rotadas.",
        "OA 18 Trasladar, rotar y reflejar figuras 2D",
        "OA 18 Demostrar que comprende el concepto de congruencia, usando la traslación, la reflexión y la rotación en cuadrículas",
        "OA 14 Realizar teselados de figuras 2D, usando traslaciones, reflexiones y rotaciones."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 34,
      "name": "OA14 Reconoce en entornos cercanos figuras 3D.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA14 Reconoce en entornos cercanos figuras 3D.",
        "OA16 Identifica ejemplos de cubos, esferas, conos, cilindros y paralelepípedos encontrados en el entorno.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 35,
      "name": "Comparan figuras 3D dadas e identifican atributos comunes y diferentes. (cubos, paralelepipedos, esferas y conos)",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Comparan figuras 3D dadas e identifican atributos comunes y diferentes. (cubos, paralelepipedos, esferas y conos)",
        "Describe figuras 3D de acuerdo a la forma de sus caras y el número de arístas y vértices (cubos, paralelepípedos, esferas, conos, cilindros y pirámides)",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 36,
      "name": "OA16 Construye figuras 3D, utilizando material concreto como plasticina, barro o masa.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA16 Construye figuras 3D, utilizando material concreto como plasticina, barro o masa.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 37,
      "name": "OA15 Reconoce líneas rectas y curvas en contextos de juegos y en figuras de 2D.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA15 Reconoce líneas rectas y curvas en contextos de juegos y en figuras de 2D.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 38,
      "name": "OA15 Completa una figura dada, utilizando líneas rectas y curvas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA15 Completa una figura dada, utilizando líneas rectas y curvas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 39,
      "name": "Identifica ejemplos de ángulos en el entorno.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Identifica ejemplos de ángulos en el entorno.",
        "Construye ángulos con el transportador",
        "No Aplica",
        "Construye ángulos agudos, obtusos, rectos, extendidos y completos con instrumentos geométricos"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 40,
      "name": "Estima la medida de ángulos, usando como referente ángulos de 45º y de 90º",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Estima la medida de ángulos, usando como referente ángulos de 45º y de 90º",
        "Compara ángulos que construye usando el transportador.",
        "No Aplica",
        "Identifica los ángulos que se forman entre dos rectas que se cortan (pares de ángulos opuestos por el vértice y pares de ángulos complementarios)"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 41,
      "name": "Demuestra de manera concreta, pictórica y simbólica que la suma de los ángulos interiores de un triángulo es de 180º y de un cuadrilátero es 360º",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Demuestra de manera concreta, pictórica y simbólica que la suma de los ángulos interiores de un triángulo es de 180º y de un cuadrilátero es 360º"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 42,
      "name": "OA10 Compara figuras 2D y 3D, que observa en forma directa o a través de TIC.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "OA10 Compara figuras 2D y 3D, que observa en forma directa o a través de TIC.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 43,
      "name": "OA10 Dibuja figuras 2D, que observa en forma directa.",
      "levels": [
        "No Aplica",
        "OA10 Dibuja figuras 2D, que observa en forma directa.",
        "OA10 Dibuja figuras 2D y construye o modela figuras 3D, que observa en forma directa o a través de TIC.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 44,
      "name": "OA10 Relaciona figuras 3D con elementos de su entorno, que observa en forma directa.",
      "levels": [
        "No Aplica",
        "OA10 Relaciona figuras 3D con elementos de su entorno, que observa en forma directa.",
        "OA10 Relaciona figuras 2D con partes de una figura 3D, que observa en forma directa o a través de TIC.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Describe dando ejemplos de artistas y vértices de figuras 3D y sus lados representando 2D, reconociendo paralelos, que se intersectan y que son perpendiculares.",
        "Contruye comparando triángulos de acuerdo a la medida de sus lagos y/o ángulos con instrumentos geométricos."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 45,
      "name": "Demuestra que comprende el concepto de área de una superficie en cubos y paralelepípedos, calculando el área de sus redes (plantillas) asociadas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Demuestra que comprende el concepto de área de una superficie en cubos y paralelepípedos, calculando el área de sus redes (plantillas) asociadas."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 46,
      "name": "i.10 OA4 Señala (indica o nombra) nociones de secuencia (antes/después/, día/noche, hoy/mañana), en situaciones cotidianas.",
      "levels": [
        "i.10 OA4 Señala (indica o nombra) nociones de secuencia (antes/después/, día/noche, hoy/mañana), en situaciones cotidianas.",
        "OA5 Señala (indica o nombra) nociones de secuencia (antes/ahora/después/al mismo tiempo, día/noche), en situaciones cotidianas.",
        "OA5 Utiliza conceptos de secuencia (antes/ahora/después/al mismo tiempo, día/noche), frecuencia (siempre/a veces/ nunca) y duración (larga/corta), al describir situaciones cotidianas.",
        "OA17 Determinan en qué momento del día se realizan ciertas actividades que son propias de la rutina diaria.",
        "OA18 Asocian eventos cotidianos con horas y medias horas determinadas (ejemplo: llegamos a clases a las 8:00 horas).",
        "Registrn el tiempo en horas, medias horas, cuartos de hora y minutos en relojes análogos y digitales.",
        "Registra diversas mediciones del tiempo en relojes análogos y digitales, usando conceptos AM, PM y 24 horas.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 47,
      "name": "Realiza conversiones entre unidades de tiempo en resoluciones de problemas (número de segundos en un minuto, minutos en una hora, días en un mes y el número de meses en un año).",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Realiza conversiones entre unidades de tiempo en resoluciones de problemas (número de segundos en un minuto, minutos en una hora, días en un mes y el número de meses en un año).",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 48,
      "name": "OA17 Identifican en el calendario fechas de días que son significativas para el alumno, el colegio y el país.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA17 Identifican en el calendario fechas de días que son significativas para el alumno, el colegio y el país.",
        "OA17 Reconocen y nombran fechas importantes con el uso del calendario.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 49,
      "name": "OA17 Secuencian fechas significativas para el alumno, usando el calendario, y las comunican con un lenguaje cotidiano.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA17 Secuencian fechas significativas para el alumno, usando el calendario, y las comunican con un lenguaje cotidiano.",
        "OA17 Nombran y ordenan los días de la semana y los meses del año.",
        "Lee e interpreta líneas de tiempo y calendarios.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 50,
      "name": "OA17 Comunican que son 7 los días de la semana y 12 los meses del año.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA17 Comunican que son 7 los días de la semana y 12 los meses del año.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 51,
      "name": "OA17 Resuelven problemas que involucren medición de tiempo. Ejemplo: ¿cuántos días o meses faltan para el día del colegio?",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA17 Resuelven problemas que involucren medición de tiempo. Ejemplo: ¿cuántos días o meses faltan para el día del colegio?",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 52,
      "name": "i.11 OA4 Ordena secuencias temporales de dos escenas o situaciones.",
      "levels": [
        "i.11 OA4 Ordena secuencias temporales de dos escenas o situaciones.",
        "OA5 Ordena secuencias temporales de tres escenas o situaciones.",
        "OA5 Ordena secuencias temporales de cuatro o más escenas o situaciones.",
        "OA2 Indican, de manera oral, el orden de acciones realizadas por ellos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 53,
      "name": "OA5 Determina la frecuencia de acciones cotidianas usando conceptos como siempre/a veces/ nunca.",
      "levels": [
        "No Aplica",
        "OA5 Determina la frecuencia de acciones cotidianas usando conceptos como siempre/a veces/ nunca.",
        "OA5 Determina la duración de dos o más acciones o situaciones al compararlas.",
        "OA16 Miden con unidades no estandarizadas (aplausos, dejar caer y tomar una pelota rítmicamente, conteo de uno en uno usando un péndulo) el tiempo necesario para realizar una tarea dada.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 54,
      "name": "OA16 Comparan eventos, usando unidades no estandarizadas de tiempo.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA16 Comparan eventos, usando unidades no estandarizadas de tiempo.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 55,
      "name": "OA16 Clasifican tareas de acuerdo al tiempo, medido con unidades no estandarizadas, que requieren para hacerlas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA16 Clasifican tareas de acuerdo al tiempo, medido con unidades no estandarizadas, que requieren para hacerlas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 56,
      "name": "OA18 Leen horas y medias horas en relojes digitales en contextos de la resolución de problemas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA18 Leen horas y medias horas en relojes digitales en contextos de la resolución de problemas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 57,
      "name": "OA18 Resuelven problemas cotidianos usando estrategias de solución que incluyen una hora o media hora.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA18 Resuelven problemas cotidianos usando estrategias de solución que incluyen una hora o media hora.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 58,
      "name": "OA11 Utiliza partes del cuerpo como unidad de medida no estandarizada, para determinar la longitud de un objeto (alto/bajo, largo/corto, ancho/angosto), en situaciones lúdicas o actividades cotidianas.",
      "levels": [
        "No Aplica",
        "OA11 Utiliza partes del cuerpo como unidad de medida no estandarizada, para determinar la longitud de un objeto (alto/bajo, largo/corto, ancho/angosto), en situaciones lúdicas o actividades cotidianas.",
        "OA11 Utiliza material concreto (lápices o palos de helado) como unidad de medida no estandarizada, para determinar la longitud de un objeto (alto/bajo, largo/corto, ancho/angosto), en situaciones lúdicas o actividades cotidianas.",
        "OA18 Comparar y medir la longitud de un objeto en medidas no estandarizadas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 59,
      "name": "OA11 Registra a través de dibujos o grafismos simples, la longitud de un objeto definida a partir de partes del cuerpo como unidad de medida no estandarizada.",
      "levels": [
        "No Aplica",
        "OA11 Registra a través de dibujos o grafismos simples, la longitud de un objeto definida a partir de partes del cuerpo como unidad de medida no estandarizada.",
        "OA11 Registra a través de dibujos, grafismos simples y números, la longitud de un objeto definida a partir del uso de material concreto como unidad de medida no estandarizada.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 60,
      "name": "OA11 Utiliza partes del cuerpo como unidad de medida no estandarizada, para comparar la longitud de dos objetos y determinar qué objeto es más alto/bajo, largo/corto, ancho/angosto, en situaciones lúdicas o actividades cotidianas.",
      "levels": [
        "No Aplica",
        "OA11 Utiliza partes del cuerpo como unidad de medida no estandarizada, para comparar la longitud de dos objetos y determinar qué objeto es más alto/bajo, largo/corto, ancho/angosto, en situaciones lúdicas o actividades cotidianas.",
        "OA11 Utiliza material concreto (lápices o palos de helado) como unidad de medida no estandarizada, para comparar la longitud de dos objetos y determinar qué objeto es más alto/bajo, largo/corto, ancho/angosto, en situaciones lúdicas o actividades cotidianas.",
        "OA18 Miden la longitud de un objeto, usando unidades de medida no estandarizadas, como lápices, clips u otros.",
        "Mide diferentes objetos, utilizando una regla o huincha (metro) y expresan sus mediciones en unidades estandarizadas.",
        "Mide y registra el perímetro de figuras regulares e irregulares del entorno en el contexto de la resolución de problemas",
        "Mide longitudes con unidades estandarizadas (m, y cm) en el contexto de la resolución de problemas.",
        "Mide longitudes con unidades estandarizadas (m, cm y mm) en el contexto de la resolución de problemas.",
        "Calcula la superficie de cubos y paralelepípedos, expresando el resultado de cm2 y m2"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 61,
      "name": "Transforma entre unidades (m a cm y viceversa), en el contexto de la resuolución de problemas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Transforma entre unidades (m a cm y viceversa), en el contexto de la resuolución de problemas.",
        "Transforma entre unidades de medidas de longuitud: km a m, m a cm, cm a mm y viceversa, de manera manual.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 62,
      "name": "Determina la longitud de dos objetos, usando unidades de medida no estandarizadas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Determina la longitud de dos objetos, usando unidades de medida no estandarizadas.",
        "Determina la longitud de objetos usando unidades de medidas estandarizadas (cm y m) resolviendo problemas.",
        "Determina el perímetro de un cuadrado y de un rectángulo.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 63,
      "name": "Compara la longitud de dos objetos, usando unidades de medida no estandarizadas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Compara la longitud de dos objetos, usando unidades de medida no estandarizadas.",
        "Resuelve problemas, comparando mediciones, y expresan la solución, usando medidas estandarizadas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 64,
      "name": "Reconoce el área de una superficie de un rectángulos y cuadrado, que mide en unidades cuadradas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Reconoce el área de una superficie de un rectángulos y cuadrado, que mide en unidades cuadradas.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 65,
      "name": "Justifica la elección de la unidad estandarizada (cm2 y m2) de rectángulos y cuadrados.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Justifica la elección de la unidad estandarizada (cm2 y m2) de rectángulos y cuadrados.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 66,
      "name": "Determina el área en cm1 y m2 en contextos cercanos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Determina el área en cm1 y m2 en contextos cercanos.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 67,
      "name": "Contruye diferentes rectángulos para un área dada (cm2 y m2) para mostrar que distintos rectángulos pueden tener la misma área.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Contruye diferentes rectángulos para un área dada (cm2 y m2) para mostrar que distintos rectángulos pueden tener la misma área.",
        "Construye diferentes rectángulos, dados el perímetros, el área o ambos y saca conclusiones.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 68,
      "name": "Realiza conteo de cuadrículas en áreas de triángulos, de paralelogramos y de trapecios, y estima áreas de figuras irregulares.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Realiza conteo de cuadrículas en áreas de triángulos, de paralelogramos y de trapecios, y estima áreas de figuras irregulares.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 69,
      "name": "Compara áreas de rectángulos en áreas de triángulos, de paralelogramos y de trapecios, y estima áreas de figuras irregulares.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Compara áreas de rectángulos en áreas de triángulos, de paralelogramos y de trapecios, y estima áreas de figuras irregulares.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 70,
      "name": "Completa figuras por traslación.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Completa figuras por traslación.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 71,
      "name": "Ordena dos o más objetos a partir de su peso de manera informal (g y kg)",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Ordena dos o más objetos a partir de su peso de manera informal (g y kg)",
        "Selecciona una unidad no estandarizada para medir el volumen de un cuerpo.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 72,
      "name": "Usa modelos para explicar la relación que existe entre gramos y kilogramos",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Usa modelos para explicar la relación que existe entre gramos y kilogramos",
        "Reconoce que el volumen se mide en unidades de cubo.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 73,
      "name": "Estima el peso de objetos de uso cotidiano, usando referentes.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Estima el peso de objetos de uso cotidiano, usando referentes.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 74,
      "name": "Mide registrando el peso de objetos en números y fracciones de uso compun, en resolución de problemas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Mide reigstrando el peso de objetos en números y fracciones de uso compun, en resolución de problemas.",
        "Mide registrando el volumen en unidades de cubo.",
        "No Aplica",
        "Calcula el volumen de cubos y paralelepípedos, expresando el resultado en cm3, m3 y mm3."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 75,
      "name": "i.12 OA5 Estima en qué grupo hay más/menos omucho/poco al observarlo.",
      "levels": [
        "i.12 OA5 Estima en qué grupo hay más/menos omucho/poco al observarlo.",
        "OA4 Estima en qué grupo hay más, menos o igual cantidad de elementos, al observarlos.",
        "OA4 Estiman cantidades de objetos con el uso de 5 como referente.",
        "OA5 Estiman cantidades de objetos, con el uso del 10 como referente.",
        "OA4 Estiman cantidades de objetos, con el uso del 10 como referente, hasta el 100",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 76,
      "name": "OA5 Seleccionan entre dos estimaciones posibles la que parece más adecuada e intentan explican la elección.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA5 Seleccionan entre dos estimaciones posibles la que parece más adecuada e intentan explican la elección.",
        "OA4 Seleccionan entre dos estimaciones posibles la que parece más adecuada y explican la elección correctamente.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 77,
      "name": "i.13 OA5 Cuenta elementos para determinar en qué grupo hay más, menos objetos, en situaciones cotidianas.",
      "levels": [
        "i.13 OA5 Cuenta elementos para determinar en qué grupo hay más, menos objetos, en situaciones cotidianas.",
        "OA4 Cuenta elementos para determinar en qué grupo hay más, menos o igual cantidad de objetos, en situaciones cotidianas.",
        "OA4 Cuenta elementos para determinar en qué grupo hay más, menos o igual cantidad de objetos o imágenes, en situaciones cotidianas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 78,
      "name": "i.14 OA5 Usa elementos concretos para representar un grupo con \"mucho\" o \"poco\" en situaciones cotidianas.",
      "levels": [
        "i.14 OA5 Usa elementos concretos para representar un grupo con \"mucho\" o \"poco\" en situaciones cotidianas.",
        "OA4 Usa elementos concretos para representar un grupo con más, menos o igual cantidad de elementos que otro, en situaciones cotidianas.",
        "OA4 Usa elementos concretos y pictóricos para representar un grupo con más, menos o igual cantidad de elementos que otro, en situaciones cotidianas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 79,
      "name": "i.15 OA5 Utiliza la expresión más/menos, mucho/poco, todo/ninguno al comparar la cantidad de dos grupos de elementos concretos.",
      "levels": [
        "i.15 OA5 Utiliza la expresión más/menos, mucho/poco, todo/ninguno al comparar la cantidad de dos grupos de elementos concretos.",
        "OA4 Utiliza la expresión más que, menos que, igual que al comparar la cantidad de dos grupos de elementos concretos.",
        "OA4 Utiliza la expresión más que, menos que, igual que al comparar la cantidad de dos o más grupos de elementos concretos y pictóricos.",
        "OA4 Usan las expresiones mayor y menor para relacionar dos cantidades, utilizando como estrategia la comparación \"uno a uno\".",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 80,
      "name": "OA4 Comparan cantidades hasta 20 en el contexto de la resolución de problemas, usando material concreto.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA4 Comparan cantidades hasta 20 en el contexto de la resolución de problemas, usando material concreto.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 81,
      "name": "OA4 Explican, usando material concreto, por qué una cantidad es mayor que otra cantidad.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA4 Explican, usando material concreto, por qué una cantidad es mayor que otra cantidad.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 82,
      "name": "OA4 Ordenan cantidades en situaciones presentadas, utilizando material de apoyo",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA4 Ordenan cantidades en situaciones presentadas, utilizando material de apoyo",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 83,
      "name": "OA4 Ordenan cantidades en el ámbito del 0 al 20 de mayor a menor o viceversa.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA4 Ordenan cantidades en el ámbito del 0 al 20 de mayor a menor o viceversa.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 84,
      "name": "i.16 OA6 Dice los números en orden desde el 1 hasta el 10 en situaciones cotidianas o juegos.",
      "levels": [
        "i.16 OA6 Dice los números en orden desde el 1 hasta el 10 en situaciones cotidianas o juegos.",
        "OA6 Dice los números en orden desde el 1 hasta el 20 en situaciones cotidianas o juegos.",
        "OA6 Dice los números en orden desde el 1 hasta el 20 y viceversa, en situaciones cotidianas o juegos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 85,
      "name": "OA6 Cuenta de 1 en 1 hasta el 20 en situaciones cotidianas o juegos.",
      "levels": [
        "No Aplica",
        "OA6 Cuenta de 1 en 1 hasta el 20 en situaciones cotidianas o juegos.",
        "OA6 Cuenta de 1 en 1, desde el 1 hasta el 20 y viceversa, en situaciones cotidianas o juegos.",
        "OA1 Cuentan de 1 en 1 desde 0 hasta el 100 y viceversa.",
        "OA1 Cuenta cantidades de elementos con el uso de grupos determinados como 2, 5, 10 y 100 elementos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 86,
      "name": "OA6 Cuenta de 2 en 2, desde el 1 hasta el 20, en situaciones cotidianas o juegos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "OA6 Cuenta de 2 en 2, desde el 1 hasta el 20, en situaciones cotidianas o juegos.",
        "OA1 Cuentan de 2 en 2, de 5 en 5 y de 10 en 10 desde 0 hasta el 100 y viceversa.",
        "OA1 Cuenta de 2 en 2, de 5 en 5, de 10 en 10 y de 100 en 100 hacia adelante y hacia atrás, hasta 999.",
        "Cuenta del 0 al 1000 de 5 en 5, de 10 en 10 y de 100 en 100 empezando por cualquier número natural menos que 1000",
        "Cuenta de 10 en 10, de 100 en 100 y de 1.000 en 1.000.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 87,
      "name": "Cuenta del 0 al 1000 de 5 en 5, de 10 en 10 y de 100 en 100, de 3 en 3, de 4 en 4 empezando por cualquier múltiplo del número correspondiente.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Cuenta del 0 al 1000 de 5 en 5, de 10 en 10 y de 100 en 100, de 3 en 3, de 4 en 4 empezando por cualquier múltiplo del número correspondiente.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 88,
      "name": "OA1 Cuentan números hacia atrás de 2 en 2, de 5 en 5 y de 10 en 10 por tramos; por ejemplo, desde 90 a 50.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA1 Cuentan números hacia atrás de 2 en 2, de 5 en 5 y de 10 en 10 por tramos; por ejemplo, desde 90 a 50.",
        "OA1 Identifican y corrigen errores y omisiones en una secuencia con a lo menos 5 números.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 89,
      "name": "i.17 OA6 Cuenta elementos concretos (entre 1 y 10) determinando la cantidad, en situaciones cotidianas o juegos.",
      "levels": [
        "i.17 OA6 Cuenta elementos concretos (entre 1 y 10) determinando la cantidad, en situaciones cotidianas o juegos.",
        "OA6 Cuenta elementos concretos (entre 1 y 20) determinando la cantidad, en situaciones cotidianas o juegos.",
        "OA6 Cuenta elementos concretos e imágenes (entre 1 y 20) determinando la cantidad, en situaciones cotidianas o juegos.",
        "OA7 Cuentan mentalmente hacia delante o hacia atrás a partir de números dados.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 90,
      "name": "0A3 Representan cantidades de manera concreta y escriben el número representado entre 0 y 20.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "0A3 Representan cantidades de manera concreta y escriben el número representado entre 0 y 20.",
        "OA2 Representan en forma concreta, pictórica y viceversa, con el uso de bloques multibase, tabla de 100, monedas y bloques apilabes",
        "No Aplica",
        "Representa de manera concretam pictórica y abrstracta los números hasta 10.000",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 91,
      "name": "Compara y ordena números de 6 dígitos y hasta 1.000 millones",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Compara y ordena números de 6 dígitos y hasta 1.000 millones",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 92,
      "name": "OA3 Lee representaciones pictóricas de números en el ámbito del 0 al 20.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA3 Lee representaciones pictóricas de números en el ámbito del 0 al 20.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 93,
      "name": "i.18 OA6 Lee algunos números hasta el 10, en situaciones cotidianas o juegos.",
      "levels": [
        "i.18 OA6 Lee algunos números hasta el 10, en situaciones cotidianas o juegos.",
        "OA6 Lee algunos números hasta el 20, en situaciones cotidianas o juegos.",
        "OA6 Lee los números hasta el 20, en situaciones cotidianas o juegos.",
        "OA3 Leen números entre 0 y 100.",
        "OA2 Leen un número dado del 0 al 100 en cifras y en palabras.",
        "Lee números hasta 1000 en cifras y palabras.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 94,
      "name": "OA2 Escriben un número dado del 0 al 100, en cifras y en palabras",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA2 Escriben un número dado del 0 al 100, en cifras y en palabras",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 95,
      "name": "i.19 OA6 Señala (indica o nombra) el orden o posición en que se encuentra un elemento concreto en una serie (Primero y último).",
      "levels": [
        "i.19 OA6 Señala (indica o nombra) el orden o posición en que se encuentra un elemento concreto en una serie (Primero y último).",
        "OA6 Señala (indica o nombra) el orden o posición en que se encuentra un elemento concreto en una serie (1° al 5°).",
        "OA6 Usa los números ordinales (al menos hasta el 5°) para describir situaciones concretas y pictóricas, que implican orden o posición.",
        "OA2 Indican la posición de números ordinales hasta el décimo; por ejemplo, el puesto de una persona en una fila.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Representa los números naturales hasta 1.000 millones dando ejemplos de estos números naturales en contextos reales",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 96,
      "name": "i.20 OA6 Indica cuál grupo tiene más, menos o la misma cantidad, al contar hasta 10 elementos concretos, en situaciones cotidianas o juegos.",
      "levels": [
        "i.20 OA6 Indica cuál grupo tiene más, menos o la misma cantidad, al contar hasta 10 elementos concretos, en situaciones cotidianas o juegos.",
        "OA6 Indica cuál grupo tiene más, menos o la misma cantidad, al contar hasta 20 elementos concretos, en situaciones cotidianas o juegos.",
        "OA6 Indica cuál grupo tiene más, menos o la misma cantidad, al contar hasta 20 elementos concretos y pictóricos, en situaciones cotidianas o juegos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 97,
      "name": "i.21 OA6 Ordena numerales desde el 1 hasta el 10 en situaciones cotidianas o juegos.",
      "levels": [
        "i.21 OA6 Ordena numerales desde el 1 hasta el 10 en situaciones cotidianas o juegos.",
        "OA6 Ordena numerales desde el 1 hasta el 20 en situaciones cotidianas o juegos.",
        "OA6 Ordena numerales desde el 1 hasta el 20 y viceversa, en situaciones cotidianas o juegos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 98,
      "name": "OA1 Cuentan con monedas hasta 500 utilizanso pesos chilenos de 1, 5, 10 , 50 y 100.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA1 Cuentan con monedas hasta 500 utilizanso pesos chilenos de 1, 5, 10 , 50 y 100.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 99,
      "name": "Resuelve problemas rutinarios en contextos cotidianos utilizando dinero, involucrando las cuatro operaciones, sin tenerlas combinadas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Resuelve problemas rutinarios en contextos cotidianos utilizando dinero, involucrando las cuatro operaciones, sin tenerlas combinadas.",
        "Resuelve problemas rutinarios en contextos cotidianos utilizando dinero, seleccionando la operación apropiada.",
        "Resuelve problemas rutinarios en contextos cotidianos utilizando dinero, combinando las cuatro operaciones.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 100,
      "name": "Resuelve problemas rutinarios en contextos, combinando las cuatro operaciones al utilizar calculadora en ámbitos numéricos superiores al 10.000",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Resuelve problemas rutinarios en contextos, combinando las cuatro operaciones al utilizar calculadora en ámbitos numéricos superiores al 10.000",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 101,
      "name": "OA3 Nombran los números que están antes y después de un número dado en la tabla de 100.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA3 Nombran los números que están antes y después de un número dado en la tabla de 100.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 102,
      "name": "OA3 Ordenan un conjunto de números dados en forma ascendente y descendente y verifican el resultado, usando cubos, la tabla de 100 y la recta numérica, utilizando como referencia el valor posicional.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA3 Ordenan un conjunto de números dados en forma ascendente y descendente y verifican el resultado, usando cubos, la tabla de 100 y la recta numérica, utilizando como referencia el valor posicional.",
        "Compara números natrurales hasta 1000, utilizando la recta numérica o tabla posicional.",
        "Compara números natrurales hasta 10.000, utilizando la recta numérica o tabla posicional.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 103,
      "name": "OA6 Incluye (nombra, indica o escribe) el numeral que falta en una secuencia numérica, entre 1 y 20, en situaciones cotidianas o juegos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "OA6 Incluye (nombra, indica o escribe) el numeral que falta en una secuencia numérica, entre 1 y 20, en situaciones cotidianas o juegos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 104,
      "name": "OA8 Agrupan una cantidad de objetos en decenas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA8 Agrupan una cantidad de objetos en decenas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 105,
      "name": "i.22 OA7 Dispone la cantidad de elementos que indica un número, de algunos números hasta el 10",
      "levels": [
        "i.22 OA7 Dispone la cantidad de elementos que indica un número, de algunos números hasta el 10",
        "OA7 Dispone la cantidad de elementos que indica un número, hasta el 10.",
        "OA7 Utiliza grafismos simples (círculos, cruces, entre otras) para representar cantidades hasta el 10.",
        "OA8 Registran de manera pictórica agrupaciones de a 10 y los elementos restantes.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 106,
      "name": "i.23 OA7 Dibuja la cantidad de elementos que indica un número, de algunos números hasta el 10.",
      "levels": [
        "i.23 OA7 Dibuja la cantidad de elementos que indica un número, de algunos números hasta el 10.",
        "OA7 Dibuja la cantidad de elementos que indica un número, hasta el 10.",
        "OA7 Escribe el número que corresponde a una cantidad, hasta 10, en situaciones lúdicas.",
        "OA8 Registran con números la cantidad de elementos de un conjunto que ha sido agrupado de a 10 y los elementos restantes.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 107,
      "name": "OA8 Cuentan en decenas y unidades, usando bloques multibase y apilables.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA8 Cuentan en decenas y unidades, usando bloques multibase y apilables.",
        "OA7 Identifican e indican las unidades y decenas de un número con el uso de material concreto como bloques apilables o dinero en el ámbito hasta 100.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 108,
      "name": "OA7 Identifican que el valor de un dígito depende de su valor posicional dentro de un numeral, hasta 100.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA7 Identifican que el valor de un dígito depende de su valor posicional dentro de un numeral, hasta 100.",
        "No Aplica",
        "Identifica el valor posicionar de los dígitos hasta la decena de mil.",
        "Identifica el valor posicional de los número naturales de hasta más de 6 dígitos y menores que 1.000 millones",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 109,
      "name": "OA7 Compone y descompone números hasta el 10, a partir de material concreto.",
      "levels": [
        "No Aplica",
        "OA7 Compone y descompone números hasta el 10, a partir de material concreto.",
        "OA7 Compone y descompone números hasta el 10, a partir de material concreto y pictórico.",
        "OA6 Representan composiciones y descomposiciones de números hasta 20 de manera pictórica.",
        "OA7 Representan un número dado hasta 100, en forma concreta, pictórica y simbólica con el uso de material multibase",
        "Representa en forma concreta, pictórica y abstracta los números hasta 1.000",
        "Representa los números hasa 10.000 de manera concreta, pictórica y abstracta.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 110,
      "name": "OA7 Indican decenas y unidades en un número de dos dígitos hasta 100.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA7 Indican decenas y unidades en un número de dos dígitos hasta 100.",
        "No Aplica",
        "Compone y descompone números naturales hasta 10.000 en forma aditiva, de acuerdo a su valor posicional.",
        "Compone los número naturales de hasta más de 6 dígitos y menores que 1.000 millones",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 111,
      "name": "Aproxima cantidades de los número naturales de hasta más de 6 dígitos y menores que 1.000 millones",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Aproxima cantidades de los número naturales de hasta más de 6 dígitos y menores que 1.000 millones",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 112,
      "name": "OA6 Determinan más de dos descomposiciones en dos grupos que se pueden hacer con un conjunto de hasta 20 elementos. Por ejemplo, 4 descomposiciones.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA6 Determinan más de dos descomposiciones en dos grupos que se pueden hacer con un conjunto de hasta 20 elementos. Por ejemplo, 4 descomposiciones.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 113,
      "name": "OA7 Describen un número dado de dos dígitos, en el ámbito hasta 99 de al menos dos formas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA7 Describen un número dado de dos dígitos, en el ámbito hasta 99 de al menos dos formas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 114,
      "name": "OA6 Componen y descomponen cantidades hasta 20 de manera simbólica.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA6 Componen y descomponen cantidades hasta 20 de manera simbólica.",
        "OA5 Componen números por medio de sumandos en forma concreta, pictórica y simbólica.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 115,
      "name": "OA5 Descomponen números en forma aditiva, concreta, pictórica y simbólica.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA5 Descomponen números en forma aditiva, concreta, pictórica y simbólica.",
        "Calcula mentalmente números hasta 100 por descomposición",
        "Calcula mentnalmente por descomposición, multiplicaciones y divisiones hasta 10X10",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 116,
      "name": "OA7 Calculan mentalmente sumas, completando 10.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA7 Calculan mentalmente sumas, completando 10.",
        "OA6 Usa la estrategia dos más, dos menos en la realización de cálculos mentales; por ejemplo, para sumar 18 + 2, piensan en 20 + 2 - 2",
        "Completa hasta la decena más cercana, mentalmente, hasta 100.",
        "No Aplica",
        "Anexa ceros cuando se multiplica mentalmente por un múltiplo de 10",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 117,
      "name": "OA7 Suman y restan mentalmente en contexto de juegos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA7 Suman y restan mentalmente en contexto de juegos.",
        "OA9 Resuelven todas las adiciones y sustracciones hasta 20 en forma mental (sin papel ni lápiz).",
        "Resuelve todas las adiciones y sustracciones hasta 100 en forma mental (sin papel ni lápiz).",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 118,
      "name": "Usa las propiedades conmutiativas, asociativas y distritrubitas mentalmente.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Usa las propiedades conmutiativas, asociativas y distritrubitas mentalmente.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 119,
      "name": "OA7 Completan dobles para sumar y restar.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA7 Completan dobles para sumar y restar.",
        "OA6 Usa dobles y mitades en calculo mental; por ejemplo, para calcular 3 + 4, piensan 3 + 3 + 1, y para calcular 5 + 6, piensan 6 + 6 - 1",
        "Usa dobles y mitades mentalmente, hasta 100",
        "Resuelve al doblar y dividir por 2 mentalmente.",
        "Dobla y divide por 2 mentalmente, en forma repetida.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 120,
      "name": "Resuelve usando el doble del doble mentalmente.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Resuelve usando el doble del doble mentalmente.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 121,
      "name": "OA6 Aplican y describen una estrategia dada para determinar una adición a partir de una sustracción; por ejemplo, para formar 16 usando la adición 9 + 7 = 16, piensan en la sustracción 16 - 9 = 7",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA6 Aplican y describen una estrategia dada para determinar una adición a partir de una sustracción; por ejemplo, para formar 16 usando la adición 9 + 7 = 16, piensan en la sustracción 16 - 9 = 7",
        "Aplica la asociabilidad realizando cálculos mentales hasta 100",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 122,
      "name": "OA9 Representan adiciones y sustracciones con material concreto, de manera pictórica y simbólica hasta 20.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA9 Representan adiciones y sustracciones con material concreto, de manera pictórica y simbólica hasta 20.",
        "OA9 Suman y restan números con resultado hasta el 100 con la aplicación del algoritmo de la adición y la sustracción.",
        "Suma y resta números con resultado hasta el 1.000 con la aplicación del algoritmo de la adición y la sustracción.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 123,
      "name": "Registra de manera simbólica adiciones y sustracciones.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Registra de manera simbólica adiciones y sustracciones.",
        "Aplica progresicamente los algoritmos con y sin reserva en la adición hasta cuatro sumandos y en la sustracción de hasta un sustraendo.",
        "Aplica los algoritmos en la adisición hasta cuatro sumandos y en la sustracción de hasta un sustrendo.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 124,
      "name": "OA10 Muestran con material concreto adiciones y sustracciones que se relacionan: por ejemplo, 3 + 4 = 7 es equivalente a la igualdad 7 - 4 = 3 y a la igualdad 7 - 3 = 4.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA10 Muestran con material concreto adiciones y sustracciones que se relacionan: por ejemplo, 3 + 4 = 7 es equivalente a la igualdad 7 - 4 = 3 y a la igualdad 7 - 3 = 4.",
        "OA10 Completan con los signos de adición (+) y sustracción (-) para que sea correcta una \"familia de operaciones\", para luego poder aplicarla en cálculos.",
        "OA13 Describen y explican una operación inversa con ayuda de las relaciones numéricas en una \"familia de operaciones\", por ejemplo 6, 7 y 13 en forma concreta, pictórica y simbólica.",
        "Fundamenta aplicando las propiedades del 0 y del 1 para la multiplicación y la propiedad del 1 para la división.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 125,
      "name": "Utiliza la relación entre la adición y la sustracción para poder formar \"familia de operaciones\" con 3 números.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Utiliza la relación entre la adición y la sustracción para poder formar \"familia de operaciones\" con 3 números.",
        "Registra de manera simbólica adiciones y sustracciones combinadas",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 126,
      "name": "OA10 Explican mediante ejemplos, con material concreto, que la adición es una operación inversa a la sustracción.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA10 Explican mediante ejemplos, con material concreto, que la adición es una operación inversa a la sustracción.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 127,
      "name": "OA10 Realizan adiciones por medio de sustracciones y viceversa, representando estas operaciones de manera pictórica. Por ejemplo, usan la resta 13 - 5 = 8 para calcular la suma 8 + 5.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA10 Realizan adiciones por medio de sustracciones y viceversa, representando estas operaciones de manera pictórica. Por ejemplo, usan la resta 13 - 5 = 8 para calcular la suma 8 + 5.",
        "No Aplica",
        "No Aplica",
        "Utiliza estrategias personales para estas operatorias.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 128,
      "name": "OA8 Suman 0 a una cantidad dada y explican que la cantidad no varía",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA8 Suman 0 a una cantidad dada y explican que la cantidad no varía",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 129,
      "name": "OA8 Nombra o señala la acción (agregar o quitar) que tiene que usar para resolver un problema de la vida cotidiana.",
      "levels": [
        "No Aplica",
        "OA8 Nombra o señala la acción (agregar o quitar) que tiene que usar para resolver un problema de la vida cotidiana.",
        "OA8 Describe la acción (agregar o quitar) que tiene que usar para resolver un problema de la vida cotidiana.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 130,
      "name": "OA9 Cuentan diferentes situaciones cotidianas donde reconocen que necesitan agregar o quitar elementos para resolver el problema.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA9 Cuentan diferentes situaciones cotidianas donde reconocen que necesitan agregar o quitar elementos para resolver el problema.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 131,
      "name": "i.24 OA8 Utiliza material concreto para resolver un problema de adición (agregando elementos hasta 5), describiendo el procedimiento empleado.",
      "levels": [
        "i.24 OA8 Utiliza material concreto para resolver un problema de adición (agregando elementos hasta 5), describiendo el procedimiento empleado.",
        "OA8 Utiliza material concreto para resolver un problema de adición (agregando elementos hasta 10), describiendo el procedimiento empleado.",
        "OA8 Utiliza material concreto y pictórico para resolver un problema de adición (agregando elementos), describiendo el procedimiento empleado.",
        "OA7 Realizan sumas en el contexto de la resolución de problemas.",
        "OA9 Resuelven problemas de adición y luego expresan la solución con el uso de algoritmos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 132,
      "name": "i.25 OA8 Utiliza material concreto para resolver un problema de sustracción (quitando elementos hasta 5), describiendo el procedimiento empleado.",
      "levels": [
        "i.25 OA8 Utiliza material concreto para resolver un problema de sustracción (quitando elementos hasta 5), describiendo el procedimiento empleado.",
        "OA8 Utiliza material concreto para resolver un problema de sustracción (quitando elementos hasta 10), describiendo el procedimiento empleado.",
        "OA8 Utiliza material concreto y pictórico para resolver un problema de sustracción (quitando o tachando elementos), describiendo el procedimiento empleado.",
        "OA7 Realizan restas en el contexto de la resolución de problemas.",
        "OA9 Resuelven problemas de sustracción y luego expresan la solución con el uso de algoritmos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 133,
      "name": "i.26 OA8 Menciona el resultado al agregar o quitar elementos a un conjunto en un ámbito numérico hasta 5.",
      "levels": [
        "i.26 OA8 Menciona el resultado al agregar o quitar elementos a un conjunto en un ámbito numérico hasta 5.",
        "OA8 Menciona el resultado al agregar o quitar elementos a un conjunto en un ámbito numérico menor a 10.",
        "OA8 Menciona o representa el resultado al agregar o quitar elementos concretos y pictóricos a un conjunto en un ámbito numérico de 10, explicando el procedimiento empleado.",
        "OA7 Realizan sumas y restas en el contexto de la resolución de problemas, argumentando sus resultados",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 134,
      "name": "OA9 Resuelven problemas que involucran sumas o restas en el ámbito hasta 20 en contextos familiares.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA9 Resuelven problemas que involucran sumas o restas en el ámbito hasta 20 en contextos familiares.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 135,
      "name": "OA2 Resuelven problemas acerca de identificaciones de números ordinales.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA2 Resuelven problemas acerca de identificaciones de números ordinales.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 136,
      "name": "OA10 Resuelven problemas, utilizando las familias de operaciones.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA10 Resuelven problemas, utilizando las familias de operaciones.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 137,
      "name": "OA8 Crea problemas simples de adición o sustracción en el ámbito numérico hasta 10, a través de material concreto o pictórico, verbalizando las estrategias que usará para resolverlos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "OA8 Crea problemas simples de adición o sustracción en el ámbito numérico hasta 10, a través de material concreto o pictórico, verbalizando las estrategias que usará para resolverlos.",
        "OA9 Crean problemas matemáticos para sumas o restas dadas en el ámbito hasta 20 con material concreto, pictórico y simbólico.",
        "OA9 Crean problemas matemáticos para adiciones y sustracciones dadas y lo resuelven, con ámbito hasta 100",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 138,
      "name": "OA9 Crean un cuento matemático para una adición dada.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA9 Crean un cuento matemático para una adición dada.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 139,
      "name": "i.27 OA10 Intenta representar o mencionar el problema concreto al que se enfrenta, en situaciones cotidianas.",
      "levels": [
        "i.27 OA10 Intenta representar o mencionar el problema concreto al que se enfrenta, en situaciones cotidianas.",
        "OA12 Menciona o representa el problema concreto al que se enfrenta, en situaciones cotidianas.",
        "OA12 Describe o representa el problema al que se enfrenta, en situaciones cotidianas.",
        "OA9 Resuelven problemas creados, correspondientes a sumas o restas que están representadas, por ejemplo, en material concreto o en láminas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 140,
      "name": "OA12 Responde preguntas sobre un problema concreto al que se enfrenta, en situaciones cotidianas.",
      "levels": [
        "No Aplica",
        "OA12 Responde preguntas sobre un problema concreto al que se enfrenta, en situaciones cotidianas.",
        "OA12 Formula preguntas, para recoger información que le permita resolver un problema al que se enfrenta, en situaciones cotidianas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 141,
      "name": "i.28 OA10 Propone algunas acciones que pueden apoyar la resolución de un problema concreto al que se enfrenta, en situaciones cotidianas.",
      "levels": [
        "i.28 OA10 Propone algunas acciones que pueden apoyar la resolución de un problema concreto al que se enfrenta, en situaciones cotidianas.",
        "OA12 Propone acciones que pueden apoyar la resolución de un problema concreto al que se enfrenta, en situaciones cotidianas.",
        "OA12 Propone diversas estrategias para resolver un problema, en situaciones cotidianas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 142,
      "name": "OA8 Sustraen 0 a una cantidad dada y explican que la cantidad no varía.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA8 Sustraen 0 a una cantidad dada y explican que la cantidad no varía.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 143,
      "name": "OA11 Expresan una multiplicación como la adición de sumandos iguales",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA11 Expresan una multiplicación como la adición de sumandos iguales",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 144,
      "name": "OA11 Explican lo que es una multiplicación con sus palabras, a partir de una situación concreta dada.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA11 Explican lo que es una multiplicación con sus palabras, a partir de una situación concreta dada.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Determina los múltiplos y los factores de números naturales menores de 100."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 145,
      "name": "OA11 Representan en forma concreta y pictórica una multiplicación dada.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA11 Representan en forma concreta y pictórica una multiplicación dada.",
        "Representa en forma concreta y poctórica las tablas de multiplicar hasta el 10.",
        "Usa estrategias para multiplicar números de tres dígitos por números de dos dígitos con o sin material concreto.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 146,
      "name": "OA11 Expresan simbólicamente diagramas que representan la multiplicación.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA11 Expresan simbólicamente diagramas que representan la multiplicación.",
        "Aplica los resultados de las tablas de multiplicación hasta 10x10, sin realizar cálculos.",
        "Multiplica números de tres dígitos por números de dos dígitos usando las tablas de multiplicación.",
        "Calcula mentalmente aplicando estrategias en la multiplicación de números naturales de dos digitos por naturales de dos dígitos",
        "Identifica números primerios y compuestos en factores y multiplos."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 147,
      "name": "Estima productos de la multiplicación de números de tres dígitos por números de dos dígitos",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Estima productos de la multiplicación de números de tres dígitos por números de dos dígitos",
        "Estima productos en la multiplicación de números naturales de dos digitos por naturales de dos dígitos",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 148,
      "name": "OA11 Construye la tabla del 2, 5 y 10, utilizando la distributividad como estrategia",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA11 Construye la tabla del 2, 5 y 10, utilizando la distributividad como estrategia",
        "Construye las tablas hasta el 10, utilizando la distribuidad.",
        "Utiliza la propiedad distributiva de la multiplicación respecto a la suma, con números de tres dígitos por números de dos dígitos.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 149,
      "name": "Aplica los algoritmos de la multiplicación de números de tres dígitos por números de dos dígitos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Aplica los algoritmos de la multiplicación de números de tres dígitos por números de dos dígitos.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 150,
      "name": "Resuelve problemas que involucran las tablas del 2, el 5 y el 10, utilizando la estrategia de ensayo y error.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Resuelve problemas que involucran las tablas del 2, el 5 y el 10, utilizando la estrategia de ensayo y error.",
        "Resuelve problemas que involucran las tablas hasta el 10.",
        "Resuelve problemas rutinarios con la multiplicación de números de tres dígitos por números de dos dígitos.",
        "resuelve problemas rutinarios y no rutinarios, utilizando el agoritmo con números naturales de dos dígitos en la multiplicación.",
        "Resuleve probmeas que involucran múltiplos."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 151,
      "name": "Representa la división como repetición y agrupación en partes iguales con material concreto y pictórico, de las tablas hasta 10x10",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Representa la división como repetición y agrupación en partes iguales con material concreto y pictórico, de las tablas hasta 10x10",
        "Demuestra la división usando estrategias para dividir con o sin material concreto, con dividendos de dos dígitpos y divisores de un dígito.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 152,
      "name": "Resuelve problemas en contextos que incluyen repartición y agrupación hasta la tabla 10x10",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Resuelve problemas en contextos que incluyen repartición y agrupación hasta la tabla 10x10",
        "No Aplica",
        "Resuelve problemas rutinarios y no rutinarios que implican divisiones con dividendos de 3 dígitos y divisores de 1 dígito.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 153,
      "name": "Crea problemas en contextos que incluyen repartición y agrupación hasta la tabla 10x10",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Crea problemas en contextos que incluyen repartición y agrupación hasta la tabla 10x10",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 154,
      "name": "Expresa la divisón como una sustracción repetida.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Expresa la divisón como una sustracción repetida.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 155,
      "name": "Describe la relación invesrsa entre la división y la multiplicación.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Describe la relación inversa entre la división y la multiplicación.",
        "Utiliza la relación que existe entre la multiplicación y la división.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 156,
      "name": "Aplica los resultados de tablas de multiplicar hasta 10x10 sin realizar cálculos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Aplica los resultados de tablas de multiplicar hasta 10x10 sin realizar cálculos.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 157,
      "name": "Estima el cociente en la división.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Estima el cociente en la división.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 158,
      "name": "Aplica la estrategia por descomposición del dividendo de dos dígitos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Aplica la estrategia por descomposición del dividendo de dos dígitos.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 159,
      "name": "Aplica el algoritmo de la división.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Aplica el algoritmo de la división.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 160,
      "name": "Interpreta el resto de las divisiones con dividendos de tres dígitos y divisores de un dígito.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Interpreta el resto de las divisiones con dividendos de tres dígitos y divisores de un dígito.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 161,
      "name": "Realiza cálculos que impliquen las cuatro operaciones, aplicando las reglas relativas a paréntesis.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Realiza cálculos que impliquen las cuatro operaciones, aplicando las reglas relativas a paréntesis.",
        "Realiza cálculos que involucren las cuatro operaciones en contexto de la resolución de problemas, utilizando calculadora en ámbitos superiores a 10.000"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 162,
      "name": "Realiza cálculos que impliquen las cuatro operaciones, aplicando las reglas relativas a prevalencia de la multiplicación y división.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Realiza cálculos que impliquen las cuatro operaciones, aplicando las reglas relativas a prevalencia de la multiplicación y división.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 163,
      "name": "Explica que una fracción representa la parte de un todo, de manera concreta, pictórica y símbolica (1/4, 1/3, 1/2, 2/3 y 3/4)",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Explica que una fracción representa la parte de un todo, de manera concreta, pictórica y símbolica (1/4, 1/3, 1/2, 2/3 y 3/4)",
        "Explica que una fracción representa la parte de un todo o de un grupo de elementos y un lugar en la recta numérica (100, 12, 10, 8, 6, 5, 4, 3, 2)",
        "Representa de manera concreta, pictórica y simbólica las fracciones propioas.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 164,
      "name": "Describe situaciones en las que se pueden usar fracciones. (1/4, 1/3, 1/2, 2/3 y 3/4)",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Describe situaciones en las que se pueden usar fracciones. (1/4, 1/3, 1/2, 2/3 y 3/4)",
        "Describe situaciones en las cuales se pueden usae las fracciones. (100, 12, 10, 8, 6, 5, 4, 3, 2)",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 165,
      "name": "Compara fracciones de un mismo todo, de igual denominador.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Compara fracciones de un mismo todo, de igual denominador.",
        "Compara y ordena fracciones con material concreto y pictórico. (Ejemplo: 1/100, 1/8, 1/5, 1/4, 1/2)",
        "Compara fracciones propias con igual y distinto denominador de manera concreta, simbólica y pictórica.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 166,
      "name": "Muestra que una fracción puede tener representaciones diferentes.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Muestra que una fracción puede tener representaciones diferentes.",
        "Crea grupos fraccionarios equivalente, simplificando y amplificando, de manera concerta, pictórica y simbólica.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 167,
      "name": "Representa fracciones propias y los números mixtos hasta el número 5, de manera concreta, pictórica y simbólica en resolución de problemas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Representa fracciones propias y los números mixtos hasta el número 5, de manera concreta, pictórica y simbólica en resolución de problemas.",
        "Representa fracciones impropias de uso común de denominadores 2, 3, 4, 5, 6, 8, 10, 12 y los números mixtos asociados usando material concreto, pictórico y simbólico.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 168,
      "name": "Identifica y determina en fracciones impropias de uso común de denominadores 2, 3, 4, 5, 6, 8, 10, 12 y los números mixtos, equivalencias entre fracciones impropias y números mixtos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Identifica y determina en fracciones impropias de uso común de denominadores 2, 3, 4, 5, 6, 8, 10, 12 y los números mixtos, equivalencias entre fracciones impropias y números mixtos.",
        "Identifica y determina entre las fracciones y los números mixtos sus equivalencias."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 169,
      "name": "Representa las fracciones impropias de uso común de denominadores 2, 3, 4, 5, 6, 8, 10, 12 y los números mixtos en la recta numérica.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Representa las fracciones impropias de uso común de denominadores 2, 3, 4, 5, 6, 8, 10, 12 y los números mixtos en la recta numérica.",
        "Representa las fracciones y los números mixtos en la recta numérica."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 170,
      "name": "Resuelve adiciones y sustracciones de fracciones con igual denominador (100, 12, 10, 8, 6, 5, 4, 3, 2) de manera concreta y pictórica en resolución de problemas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Resuelve adiciones y sustracciones de fracciones con igual denominador (100, 12, 10, 8, 6, 5, 4, 3, 2) de manera concreta y pictórica en resolución de problemas.",
        "Resuelve adiciones y sustracciones con fracciones propias con denominadoras menoras o igules a 12 de manera pictórica y simbólica.",
        "Resuelve adiciones y sustracciones de fracciones propias e impropias y números mixtos con numeradores y denominadores de hasta dos dígitos."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 171,
      "name": "Resuelve adiciones y sustracciones con fracciones propias con denominadoras menoras o igules a 12 ampliando o simplicando.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Resuelve adiciones y sustracciones con fracciones propias con denominadoras menoras o igules a 12 ampliando o simplicando.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 172,
      "name": "Representa décimos y centésimos en forma concreta, pictórica y simbólica.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Representa décimos y centésimos en forma concreta, pictórica y simbólica.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 173,
      "name": "Compara y ordena decimales hasta la centécima.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Compara y ordena decimales hasta la centécima.",
        "Compara y ordena decimales hasta la milésima",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 174,
      "name": "Determina el decimal que corresponde a fracciones con denominador 2,4,5 y 10.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Determina el decimal que corresponde a fracciones con denominador 2,4,5 y 10.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 175,
      "name": "Resuelve adiciones y sustracciones de decimales, considerando el valor posicional hasta centécima durante la resolución de problemas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Resuelve adiciones y sustracciones de decimales, considerando el valor posicional hasta centécima durante la resolución de problemas.",
        "Resuelve adiciones y sustracciones de decimales, empleando el valor posicional hasta la milésima.",
        "Resuelve la multiplicación y división de decimales por números naturales de un dígito, multiplos de 10 y decimales hasta milésima de manera concreta, pictórica y simbólica."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 176,
      "name": "Resuelve problemas rutinarios y no rutinarios, aplicando adiciones y sustracciones de fracciones propias o decimales hasta milésima",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Resuelve problemas rutinarios y no rutinarios, aplicando adiciones y sustracciones de fracciones propias o decimales hasta milésima",
        "Resuelve problemas rutinarios y no rutinarios que involucren adiciones y sustracciones de fracciones propias, impropias números mixtos o decmales hasta la milésima."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 177,
      "name": "OA12 Representa (nombra o dibuja) la solución de un problema al que se enfrenta, describiendo las acciones que realiza para resolverlo.",
      "levels": [
        "No Aplica",
        "OA12 Representa (nombra o dibuja) la solución de un problema al que se enfrenta, describiendo las acciones que realiza para resolverlo.",
        "OA12 Representa (nombra, dibuja o registra en tablas o gráficos) la solución de un problema al que se enfrenta, explicando por qué ciertas acciones favorecieron o dificultaron el proceso.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 178,
      "name": "OA19 Recolectan y organizan datos de si mismos y del entorno, usando material concreto y pictórico, registros informales y tablas de conteo.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA19 Recolectan y organizan datos de si mismos y del entorno, usando material concreto y pictórico, registros informales y tablas de conteo.",
        "OA20 Recolectan datos acerca de lanzamientos de dados y monedas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 179,
      "name": "OA19 Formulan preguntas sobre sí mismos y los demás que pueden ser respondidas a partir de recolección de información.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA19 Formulan preguntas sobre sí mismos y los demás que pueden ser respondidas a partir de recolección de información.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 180,
      "name": "OA19 Responden preguntas, utilizando la información recolectada.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA19 Responden preguntas, utilizando la información recolectada.",
        "OA20 Responden preguntas en el contexto de juegos con monedas, usando registros expresados en cubos apilables.",
        "Ordena datos obtenidos de juegos aleatorios con dados y monedas, encontrando al menor, el mayor y estimando el punto medio entre ambos.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 181,
      "name": "OA19 Registran datos, usando bloques y tablas de conteo.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA19 Registran datos, usando bloques y tablas de conteo.",
        "OA20 Registran datos en una tabla de conteo acerca de datos de lanzamientos de monedas y dados.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 182,
      "name": "OA20 Registran datos acerca de lanzamientos de dados y monedas, usando cubos apilables.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA20 Registran datos acerca de lanzamientos de dados y monedas, usando cubos apilables.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 183,
      "name": "OA20 Registran datos acerca de lanzamientos de dados y monedas, usando cubos apilables.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA20 Registran datos acerca de lanzamientos de dados y monedas, usando cubos apilables.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 184,
      "name": "OA21 Registran resultados de juegos aleatorios con dados y monedas en tablas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA21 Registran resultados de juegos aleatorios con dados y monedas en tablas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 185,
      "name": "OA21 Registran resultados de juegos aleatorios con dados y monedas en gráficos de barra simple.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA21 Registran resultados de juegos aleatorios con dados y monedas en gráficos de barra simple.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 186,
      "name": "OA20 Leen pictogramas que contiene información dada.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA20 Leen pictogramas que contiene información dada.",
        "OA22 Lee pictogramas donde la figura representa más de una unidad.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 187,
      "name": "OA20 Interpretan información representada en pictogramas y responden preguntas de acuerdo a esa interpretación.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA20 Interpretan información representada en pictogramas y responden preguntas de acuerdo a esa interpretación.",
        "OA22 Interpreta pictogramas donde la figura representa más de una unidad y luego responde preguntas.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 188,
      "name": "OA22 Determinan las características de un pictograma, usando correspondencia uno a uno o unos a varios.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA22 Determinan las características de un pictograma, usando correspondencia uno a uno o unos a varios.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 189,
      "name": "OA20 Construyen pictogramas de acuerdo a información presentada de manera concreta y pictórica y responden preguntas basados en el pictograma.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA20 Construyen pictogramas de acuerdo a información presentada de manera concreta y pictórica y responden preguntas basados en el pictograma.",
        "OA22 Construyen un pictograma a partir de datos obtenidos de su entorno.",
        "Realiza encuestas clasificando los datos obtenidos en tablas visualizando en gráficos de barra.",
        "Analiza datos realizado en encuestas y compara con los resultados de muestra aleatoreias, usando tablas y gráficos",
        "Utiliza diagramas de tallo y hojas para representar datos provenientes de muestras aleatorias.",
        "Compara disfribuciones de dos grupos, provenientes de muestras aleatorias, usando diagramas de puntos y de tallos y hojas."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 190,
      "name": "Calcula el promedio de datos y lo interpreta en su contexto.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Calcula el promedio de datos y lo interpreta en su contexto.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 191,
      "name": "OA22 Leen gráficos de barra simple dados y luego responden preguntas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA22 Leen gráficos de barra simple dados y luego responden preguntas.",
        "Lee e interpreta pictogramas y gráficos de barra simple con escala, en base a la información recolectada o dada.",
        "Realiza experimentos aleatorios lúdicos tabulando y representando mediante gráficos.",
        "Describe la psobilidad de ocurrencia de un evento, empleando los términos seguro - posible - poco posible - imposible.",
        "Conjetura acerca de la tendencia de resultados obtenidos en repeticiones de un mimso experimento con dados, monedas u otros."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 192,
      "name": "Compara probabilidades de disntitos eventos sin calculadora.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Compara probabilidades de distintos eventos sin calculadora.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 193,
      "name": "OA22 Determinan las características de un gráfico de barras simple.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA22 Determinan las características de un gráfico de barras simple.",
        "Representa datos usando diagrama de puntos.",
        "Interpretan pictogramas y gráficos de barra simple con escala.",
        "Interpreta y completa tablas, gráficos de barra simple y gráficos de línea.",
        "Interpreta gráficos de barra doble y circulares."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 194,
      "name": "OA22 Construyen, usando material concreto, un gráfico de barras simple con información recolectada y dada y luego responden preguntas.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "OA22 Construyen, usando material concreto, un gráfico de barras simple con información recolectada y dada y luego responden preguntas.",
        "No Aplica",
        "Comunica conclusiones al interpretar los pictogramas y gráficos.",
        "Comunica conclusiones obtenidas en sus gráficos de línea.",
        "Comunica sus conclusiones en relación a gráficos de barra doble y circulares."
      ],
      "selectedLevelIndex": null
    },
  ]
};

const PensamientoMatematico: React.FC<{
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number) => void;
  handleNotEvaluatedChange?: (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => void;
  viewMode: 'student' | 'skill';
  handleViewModeChange: (mode: 'student' | 'skill') => void;
  showInstructions: boolean;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
  handleStudentSelect: (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}> = ({
  students,
  selectedStudentIds,
  handleLevelChange,
  handleNotEvaluatedChange,
  viewMode,
  handleViewModeChange,
  showInstructions,
  selectedCourse,
  setSelectedCourse,
  courses,
  handleStudentSelect,
  getExpectedLevel,
  getPreviousYearLevel
}) => {
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("lastName");
  const [showAllYears, setShowAllYears] = useState(true);
  const [skillsLoaded, setSkillsLoaded] = useState(true); // Ya están cargados desde el fallback
  
  // Estado para rastrear niveles previamente seleccionados
  const [previouslySelectedLevels, setPreviouslySelectedLevels] = useState<{[key: string]: number | null}>();

  // Funciones de utilidad para colores
  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      if (percentage >= 75) return 'bg-green-600 text-white border-green-600';
      if (percentage >= 50) return 'bg-blue-600 text-white border-blue-600';
      if (percentage >= 25) return 'bg-yellow-600 text-white border-yellow-600';
      return 'bg-red-600 text-white border-red-600';
    }
    if (percentage >= 75) return 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200';
    if (percentage >= 50) return 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200';
    if (percentage >= 25) return 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200';
    return 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200';
  };

  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) return 'text-white';
    if (percentage >= 75) return 'text-green-700';
    if (percentage >= 50) return 'text-blue-700';
    if (percentage >= 25) return 'text-yellow-700';
    return 'text-red-700';
  };

  // Función para extraer el primer apellido
  // Formato esperado: "Apellido Paterno; Apellido Materno; Nombre"
  const getFirstLastName = (fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // El primer apellido (apellido paterno) es la primera parte del nombre
    if (nameParts.length >= 1) {
      return nameParts[0].toLowerCase();
    }
    // Si está vacío, retornamos la cadena vacía
    return '';
  };

  const getSkillLevel = (student: Student, dimensionId: number, skillId: number): number | null => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.level ?? null;
  };

  // 🚫 Función para verificar si una habilidad está marcada como "No evaluada"
  const isSkillNotEvaluated = (student: Student, dimensionId: number, skillId: number): boolean => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.notEvaluated === true;
  };

  // 🎯 NUEVO: Helper compartido para obtener el nivel del año anterior REAL
  const getHistoricalLevel = useCallback((student: Student, skillId: number, currentAge: number) => {
    if (student.previousYearLevels && student.previousYearLevels.length > 0) {
      const historical = student.previousYearLevels.find(
        pl => pl.dimensionId === pensamientoMatematicosDimension.id && pl.skillId === skillId
      );
      if (historical !== undefined && historical.level !== null) {
        return historical.level;
      }
    }
    return getPreviousYearLevel(currentAge);
  }, []);

  useEffect(() => {
    if (viewMode === 'student' && selectedStudentIds.length === 0 && students.length > 0) {
      // Usar el primer estudiante disponible del curso actual, no hardcodear
      const firstStudent = students[0];
      if (firstStudent) {
        handleStudentSelect(new MouseEvent('click') as any, firstStudent.id as any);
      }
    } else if (viewMode === 'skill' && !selectedSkillId) {
      const firstSkill = pensamientoMatematicosDimension.skills[0];
      if (firstSkill) {
        setSelectedSkillId(firstSkill.id);
      }
    }
  }, [viewMode, students, selectedStudentIds, handleStudentSelect, selectedSkillId]);

  const SkillsChart = useCallback(({ student }: { student: Student }) => {
    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    // Usar directamente pensamientoMatematicosDimension.skills
    const data = pensamientoMatematicosDimension.skills.map(skill => ({
      name: skill.name,
      previousYearLevel: calculateLastYearPercentage(age),
      percentageAchieved: getSkillLevel(student, pensamientoMatematicosDimension.id, skill.id) === null 
        ? 0 
        : (getSkillLevel(student, pensamientoMatematicosDimension.id, skill.id)! + 1) * 20,
      expectedLevel: calculateExpectedLevel(age),
    }));

    const maxSkillNameLength = Math.max(...data.map(d => d.name.length));
    const yAxisWidth = Math.min(200, maxSkillNameLength * 8);
    const barSize = 40;
    const minChartHeight = 200;
    const heightPerItem = 60;
    const chartHeight = Math.max(minChartHeight, data.length * heightPerItem);

    const wrapText = (text: string, maxWidth: number) => {
      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = getTextWidth(currentLine + " " + word);
        if (width < maxWidth) {
          currentLine += " " + word;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    const getTextWidth = (text: string) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        context.font = '14px Arial';
        return context.measureText(text).width;
      }
      return 0;
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-bold mb-4">Gráfico de {pensamientoMatematicosDimension.name}</h3>
        <div style={{ width: '100%', height: `${chartHeight}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              layout="vertical"
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis 
                type="number" 
                domain={[0, 110]}
                ticks={[20, 60, 100]}
                tickFormatter={(value) => {
                  const ageMap: { [key: number]: string } = {
                    20: 'Hacia los 4 años',
                    60: 'Hacia los 6 años',
                    100: 'Hacia los 8 años'
                  };
                  return ageMap[value] || '';
                }}
                tick={{ fontSize: 14, fontWeight: 'bold' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={yAxisWidth}
                tick={({ x, y, payload }) => {
                  const lines = wrapText(payload.value, yAxisWidth - 20);
                  return (
                    <g transform={`translate(${x},${y})`}>
                      {lines.map((line, index) => (
                        <text
                          key={`item-${line}-${index}`}
                          x={-10}
                          y={index * 16}
                          dy={-8 * (lines.length - 1)}
                          textAnchor="end"
                          fill="#333"
                          fontSize={14}
                          fontWeight="bold"
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  );
                }}
                axisLine={false}
                tickLine={false}
              />
              <ZAxis range={[200, 200]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="percentageAchieved" 
                fill={COLORS.achieved} 
                name="Nivel habilidad logrado" 
                barSize={barSize}
              />
              <Bar
                dataKey="expectedLevel"
                fill={COLORS.expected}
                name="Nivel esperado"
                barSize={4}
                shape={<CustomBar />}
              />
              <Scatter 
                dataKey="previousYearLevel" 
                fill={COLORS.previousYear} 
                name="Nivel año anterior" 
                shape={<CustomVerticalRectangle />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }, [getSkillLevel]);

  const DimensionSummary = useCallback(({ student, dimensionId }: DimensionSummaryProps) => {
    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    // Calcular el porcentaje logrado usando skillLevels
    const percentageAchieved = pensamientoMatematicosDimension.skills.reduce((sum, skill) => {
      const level = getSkillLevel(student, dimensionId, skill.id);
      // ✅ CORRECCIÓN: Añadir año base para alinear con edad real seleccionada
      return sum + (level === null ? 0 : (level + 1) * 20 + 20);
    }, 0) / pensamientoMatematicosDimension.skills.length;

    const data = [{
      name: pensamientoMatematicosDimension.name,
      previousYearLevel: calculateLastYearPercentage(age),
      percentageAchieved,
      expectedLevel: calculateExpectedLevel(age),
    }];

    const maxSkillNameLength = Math.max(...data.map(d => d.name.length));
    const yAxisWidth = Math.min(200, maxSkillNameLength * 8);
    const barSize = 40;
    const minChartHeight = 200;
    const heightPerItem = 60;
    const chartHeight = Math.max(minChartHeight, data.length * heightPerItem);

    const wrapText = (text: string, maxWidth: number) => {
      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = getTextWidth(currentLine + " " + word);
        if (width < maxWidth) {
          currentLine += " " + word;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    const getTextWidth = (text: string) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        context.font = '14px Arial';
        return context.measureText(text).width;
      }
      return 0;
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-bold mb-4">Resumen de {pensamientoMatematicosDimension.name}</h3>
        <div style={{ width: '100%', height: `${chartHeight}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              layout="vertical"
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis 
                type="number" 
                domain={[0, 110]}
                ticks={[20, 60, 100]}
                tickFormatter={(value) => {
                  const ageMap: { [key: number]: string } = {
                    20: 'Hacia los 4 años',
                    60: 'Hacia los 6 años',
                    100: 'Hacia los 8 años'
                  };
                  return ageMap[value] || '';
                }}
                tick={{ fontSize: 14, fontWeight: 'bold' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={yAxisWidth}
                tick={({ x, y, payload }) => {
                  const lines = wrapText(payload.value, yAxisWidth - 20);
                  return (
                    <g transform={`translate(${x},${y})`}>
                      {lines.map((line, index) => (
                        <text
                          key={`item-${line}-${index}`}
                          x={-10}
                          y={index * 16}
                          dy={-8 * (lines.length - 1)}
                          textAnchor="end"
                          fill="#333"
                          fontSize={14}
                          fontWeight="bold"
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  );
                }}
                axisLine={false}
                tickLine={false}
              />
              <ZAxis range={[200, 200]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="percentageAchieved" 
                fill={COLORS.achieved} 
                name="Nivel habilidad logrado" 
                barSize={barSize}
              />
              <Bar
                dataKey="expectedLevel"
                fill={COLORS.expected}
                name="Nivel esperado"
                barSize={4}
                shape={<CustomBar />}
              />
              <Scatter 
                dataKey="previousYearLevel" 
                fill={COLORS.previousYear} 
                name="Nivel año anterior" 
                shape={<CustomVerticalRectangle />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }, [getSkillLevel]);

  // Modificar la función selectColumnForAllStudents para procesar en lotes
  const selectColumnForAllStudents = (levelIndex: number, students: Student[], selectedSkillId: number, handleLevelChange: Function) => {
    // Verificar si todos los estudiantes tienen este nivel seleccionado
    const allSelected = students.every(student =>
      student.skillLevels.find(
        sl => sl.dimensionId === pensamientoMatematicosDimension.id &&
             sl.skillId === selectedSkillId &&
             sl.level === levelIndex
      )
    );

    // Procesar en lotes de 20 estudiantes
    const batchSize = 20;
    const processStudentsInBatches = async () => {
      for (let i = 0; i < students.length; i += batchSize) {
        const batch = students.slice(i, i + batchSize);

        // Pequeña pausa entre lotes para no sobrecargar
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        batch.forEach(student => {
          handleLevelChange(
            student.id,
            pensamientoMatematicosDimension.id,
            selectedSkillId,
            allSelected ? -1 : levelIndex
          );
        });
      }
    };

    processStudentsInBatches();
  };

  // Modificar la función selectColumnForAllSkills de manera similar
  const selectColumnForAllSkills = (levelIndex: number, studentId: number | string, handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, level: number | null) => void) => {
    const batchSize = 20;
    const skills = pensamientoMatematicosDimension.skills;

    const processSkillsInBatches = async () => {
      for (let i = 0; i < skills.length; i += batchSize) {
        const batch = skills.slice(i, i + batchSize);

        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        batch.forEach(skill => {
          const currentLevel = students.find(s => s.id === studentId)?.skillLevels.find(
            sl => sl.dimensionId === pensamientoMatematicosDimension.id && sl.skillId === skill.id
          )?.level;

          handleLevelChange(
            studentId,
            pensamientoMatematicosDimension.id,
            skill.id,
            currentLevel === levelIndex ? -1 : levelIndex
          );
        });
      }
    };

    processSkillsInBatches();
  };

  const handleLocalLevelChange = useCallback((studentId: number | string, skillId: number, levelIndex: number | null) => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
    console.log('🔍 PensamientoMatematico handleLocalLevelChange:', {
      studentId,
      skillId,
      levelIndex,
      dimensionId: pensamientoMatematicosDimension.id,
      timestamp: new Date().toISOString()
    });

    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (student) {
      const currentLevel = getSkillLevel(student, pensamientoMatematicosDimension.id, skillId);

      if (currentLevel !== null && !previouslySelectedLevels?.[`${studentId}-${skillId}`]) {
        setPreviouslySelectedLevels(prev => ({
          ...prev,
          [`${studentId}-${skillId}`]: currentLevel
        }));
      }

      // Lógica de toggle: si el nivel ya está seleccionado, deseleccionarlo
      if (currentLevel === levelIndex) {
        console.log('🔄 Deseleccionando nivel existente:', { studentId, skillId, currentLevel });
        handleLevelChange(studentId, pensamientoMatematicosDimension.id, skillId, null);
        return;
      }
    }

    // Si levelIndex es -1, convertir a null (para compatibilidad con el botón de reiniciar)
    const actualLevelIndex = levelIndex === -1 ? null : levelIndex;
    console.log('📝 Aplicando nuevo nivel:', { studentId, skillId, actualLevelIndex });
    handleLevelChange(studentId, pensamientoMatematicosDimension.id, skillId, actualLevelIndex);
  }, [handleLevelChange, students, previouslySelectedLevels]);

  // useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    if (skillsLoaded && students.length > 0) {
      // Inicializar los niveles previamente guardados en la base de datos
      const initialLevels: {[key: string]: number} = {};
      
      students.forEach(student => {
        student.skillLevels.forEach(skillLevel => {
          if (skillLevel.dimensionId === pensamientoMatematicosDimension.id && skillLevel.level !== null) {
            initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
          }
        });
      });
      
      // Solo actualizar si hay datos que inicializar
      if (Object.keys(initialLevels).length > 0) {
        setPreviouslySelectedLevels(initialLevels);
      }
    }
  }, [students, skillsLoaded]);

  const renderStudentView = useMemo(() => {
    return () => selectedStudentIds.map(studentId => {
      const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
      const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
      if (!student) return null;

      const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

      const expectedLevel = getExpectedLevel(age);

      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        const studentAge = age;
        const minAge = Math.max(4, studentAge - 2);
        const maxAge = Math.min(12, studentAge + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

      return (
        <div key={student.id} className="mb-8">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Progreso de habilidades:</span>
                    <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                      {(() => {
                        // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                        const progress = calculateFormProgress(
                          pensamientoMatematicosDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        const progressPercent = progress.percentage;
                        
                        let barColor = "bg-red-500";
                        if (progressPercent >= 75) barColor = "bg-green-500";
                        else if (progressPercent >= 50) barColor = "bg-blue-500";
                        else if (progressPercent >= 25) barColor = "bg-yellow-500";
                        
                        return (
                          <div 
                            className={`h-2.5 rounded-full ${barColor}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        );
                      })()}
                    </div>
                    <span className="text-xs text-gray-500">
                      {(() => {
                        const progress = calculateFormProgress(
                          pensamientoMatematicosDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        return `${progress.completedSkills} de ${progress.totalValidSkills} habilidades`;
                      })()}
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowAllYears(!showAllYears)}
                      className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                        showAllYears 
                          ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                          : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                    </button>
                    
                    <button
                      onClick={() => {
                        if (window.confirm("¿Seguro que desea reiniciar todos los niveles de habilidad para este estudiante?")) {
                          pensamientoMatematicosDimension.skills.forEach(skill => {
                            handleLocalLevelChange(student.id, skill.id, -1);
                          });
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                    >
                      Reiniciar
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                    <tr>
                      <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">Habilidad</th>
                      {visibleYears.map((ageGroup, index) => {
                        const actualIndex = visibleIndices[index];
                        return (
                    <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                      <div className="flex flex-col items-center">
                              <span className="mb-1">{ageGroup}</span>
                        <button
                                onClick={() => {
                                  pensamientoMatematicosDimension.skills.forEach(skill => {
                                    const currentLevel = getSkillLevel(student, pensamientoMatematicosDimension.id, skill.id);
                                    if (currentLevel === actualIndex) {
                                      handleLocalLevelChange(student.id, skill.id, null);
                                    } else {
                                      handleLocalLevelChange(student.id, skill.id, actualIndex);
                                    }
                                  });
                                }}
                                className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                        >
                          Seleccionar todo
                        </button>
                      </div>
                    </th>
                        );
                      })}
                </tr>
              </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                {pensamientoMatematicosDimension.skills.map((skill: Skill, skillIndex: number) => {
                  const selectedLevel = getSkillLevel(student, pensamientoMatematicosDimension.id, skill.id);
                  const skillIsNotEvaluated = isSkillNotEvaluated(student, pensamientoMatematicosDimension.id, skill.id);
                  
                  const previousYearLevel = getHistoricalLevel(student, skill.id, age);

                  return (
                        <tr key={skill.id} className={`${skillIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                          <td className="py-3 px-6 text-sm font-medium text-gray-900">
                            <div className="flex items-start">
                              <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                                {skillIndex + 1}
                              </span>
                              <div className="flex-1">
                                {skill.name}
                                {selectedLevel !== null && !skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    Nivel seleccionado: {selectedLevel + 1}
                                  </div>
                                )}
                                {skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    🚫 No evaluada
                                  </div>
                                )}
                                {/* Botón para marcar como No Evaluada */}
                                {handleNotEvaluatedChange && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleNotEvaluatedChange(student.id, pensamientoMatematicosDimension.id, skill.id, !skillIsNotEvaluated)}
                                      className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                        skillIsNotEvaluated
                                          ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                      }`}
                                      title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                    >
                                      {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                            const level = skill.levels[levelIndex];
                            if (!level) return null;
                            
                            const isNotApplicable = level.toLowerCase().includes('no aplica');
                            const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                            
                            let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                            
                            const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                            const isExpected = levelIndex === expectedLevel;
                            const isPrevious = levelIndex === previousYearLevel;

                            // 1. Fondo según selección o estado base
                            if (isNotApplicable) {
                              // Si es "No aplica", verificar si está seleccionado
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.notApplicableSelected;
                              } else {
                                buttonClass += LEVEL_COLORS.notApplicable;
                              }
                            } else if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.selected;
                            } else if (previouslySelected) {
                              buttonClass += LEVEL_COLORS.previouslySelected;
                            } else {
                              buttonClass += LEVEL_COLORS.default;
                            }

                            // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                            let extraStyles: React.CSSProperties = {
                              boxSizing: 'border-box'
                            };

                            if (isPrevious) {
                              // Aplicar borde gris grueso para nivel año anterior
                              extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                              }
                            }

                            if (isExpected) {
                              // Si es esperado, aplicar borde amarillo
                              if (!isPrevious) {
                                extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                                if (selectedLevel !== levelIndex) {
                                  extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                                }
                              }
                            }

                        return (
                              <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                                <button
                                  onClick={() => handleLocalLevelChange(student.id, skill.id, levelIndex)}
                                  className={buttonClass}
                                  style={extraStyles}
                                  title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                                >
                                  <span className="text-center leading-tight">{level}</span>
                                </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
              </div>
              
              {/* Controles de navegación rápida */}
              <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Volver arriba
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [selectedStudentIds, students, getExpectedLevel, getPreviousYearLevel, showAllYears, handleLocalLevelChange, previouslySelectedLevels]);

  // Estudiantes filtrados y ordenados
  const filteredStudents = useMemo(() => {
    return students
      .filter(student => selectedCourse ? student.course === selectedCourse : true)
      .filter(student => 
        searchTerm === '' || 
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.course.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        if (sortBy === 'lastName') {
          const lastNameA = getFirstLastName(a.name);
          const lastNameB = getFirstLastName(b.name);
          return lastNameA.localeCompare(lastNameB);
        }
        if (sortBy === 'course') return a.course.localeCompare(b.course);
        return 0;
      });
  }, [students, selectedCourse, searchTerm, sortBy, getFirstLastName]);

  const renderSkillView = useMemo(() => {
    return () => {
      if (!selectedSkillId) {
        return (
          <div className="text-center py-8 text-gray-500">
            Selecciona una habilidad para comenzar
          </div>
        );
      }
      
      const skill = pensamientoMatematicosDimension.skills.find(s => s.id === selectedSkillId);
      if (!skill) return null;

      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        const averageAge = filteredStudents.length > 0 ? 
          filteredStudents.reduce((sum, student) => {
            const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
            return sum + age;
          }, 0) / filteredStudents.length : 6;
        
        const minAge = Math.max(4, Math.floor(averageAge) - 2);
        const maxAge = Math.min(12, Math.floor(averageAge) + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

      // Estado masivo de "No evaluada" para la habilidad seleccionada
      const allStudentsNotEvaluated =
        filteredStudents.length > 0 &&
        filteredStudents.every(student =>
          isSkillNotEvaluated(student, pensamientoMatematicosDimension.id, skill.id)
        );

      const toggleNotEvaluatedForAll = () => {
        if (!handleNotEvaluatedChange) return;
        const nextState = !allStudentsNotEvaluated;
        filteredStudents.forEach(student => {
          handleNotEvaluatedChange(
            student.id,
            pensamientoMatematicosDimension.id,
            selectedSkillId,
            nextState
          );
        });
      };

      return (
        <div className="mb-8">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Progreso de la habilidad:</span>
                    <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                      {(() => {
                        const totalStudents = filteredStudents.length;
                        const completedStudents = filteredStudents.filter(student => 
                          getSkillLevel(student, pensamientoMatematicosDimension.id, skill.id) !== null
                        ).length;
                        const progressPercent = totalStudents > 0 ? (completedStudents / totalStudents) * 100 : 0;
                        
                        let barColor = "bg-red-500";
                        if (progressPercent >= 75) barColor = "bg-green-500";
                        else if (progressPercent >= 50) barColor = "bg-blue-500";
                        else if (progressPercent >= 25) barColor = "bg-yellow-500";
                        
                        return (
                          <div 
                            className={`h-2.5 rounded-full ${barColor}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        );
                      })()}
                    </div>
                    <span className="text-xs text-gray-500">
                      {filteredStudents.filter(student => 
                        getSkillLevel(student, pensamientoMatematicosDimension.id, skill.id) !== null
                      ).length} de {filteredStudents.length} estudiantes
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{skill.name}</span>
                    <button
                      onClick={() => setShowAllYears(!showAllYears)}
                      className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                        showAllYears 
                          ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                          : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                      <div className="flex flex-col items-start gap-1">
                        <span>Estudiante</span>
                        {handleNotEvaluatedChange && (
                          <button
                            onClick={toggleNotEvaluatedForAll}
                            className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all border ${
                              allStudentsNotEvaluated
                                ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                : "bg-white text-indigo-900 border-white/50 hover:bg-indigo-50"
                            }`}
                            title="Marcar esta habilidad como no evaluada para todos los estudiantes filtrados"
                          >
                            {allStudentsNotEvaluated
                              ? "✓ Habilitar evaluación"
                              : "🚫 No evaluar esta habilidad"}
                          </button>
                        )}
                      </div>
                    </th>
                      {visibleYears.map((ageGroup, index) => {
                        const actualIndex = visibleIndices[index];
                        return (
                    <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                      <div className="flex flex-col items-center">
                              <span className="mb-1">{ageGroup}</span>
                        <button
                                onClick={() => {
                                  filteredStudents.forEach(student => {
                                    const currentLevel = getSkillLevel(student, pensamientoMatematicosDimension.id, selectedSkillId);
                                    if (currentLevel === actualIndex) {
                                      handleLocalLevelChange(student.id as any, selectedSkillId, null);
                                    } else {
                                      handleLocalLevelChange(student.id as any, selectedSkillId, actualIndex);
                                    }
                                  });
                                }}
                                className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                        >
                          Seleccionar todo
                        </button>
                      </div>
                    </th>
                        );
                      })}
                </tr>
              </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredStudents.map((student, studentIndex) => {
                      const selectedLevel = getSkillLevel(student, pensamientoMatematicosDimension.id, selectedSkillId);
                      const skillIsNotEvaluated = isSkillNotEvaluated(student, pensamientoMatematicosDimension.id, selectedSkillId);
                      const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

                    return (
                        <tr key={student.id} className={`${studentIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                          <td className="py-3 px-6 text-sm font-medium text-gray-900">
                            <div className="flex items-start">
                              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-2">
                                <span className="text-indigo-800 font-bold text-xs">{student.name.split(' ')[0][0]}{student.name.split(' ')[1]?.[0]}</span>
                              </div>
                              <div className="ml-1 flex-1">
                                <div className="font-medium">{student.name}</div>
                                <div className="text-xs text-gray-500">{student.course}</div>
                                {selectedLevel !== null && !skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    Nivel seleccionado: {selectedLevel + 1}
                                  </div>
                                )}
                                {skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    🚫 No evaluada
                                  </div>
                                )}
                                {/* Botón para marcar como No Evaluada */}
                                {handleNotEvaluatedChange && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleNotEvaluatedChange(student.id, pensamientoMatematicosDimension.id, selectedSkillId, !skillIsNotEvaluated)}
                                      className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                        skillIsNotEvaluated
                                          ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                      }`}
                                      title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                    >
                                      {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                            const level = skill.levels[levelIndex];
                            if (!level) return null;
                            
                            const isNotApplicable = level.toLowerCase().includes('no aplica');
                            const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                            
                            let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                            
                            const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                            const expectedLevel = getExpectedLevel(age);

                            if (isNotApplicable) {
                              // Si es "No aplica", verificar si está seleccionado
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.notApplicableSelected;
                              } else {
                                buttonClass += LEVEL_COLORS.notApplicable;
                              }
                            } else {
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.selected;
                              } else if (previouslySelected) {
                                buttonClass += LEVEL_COLORS.previouslySelected;
                              } else if (levelIndex === expectedLevel) {
                                buttonClass += LEVEL_COLORS.expected;
                              } else if (levelIndex === previousYearLevel) {
                                buttonClass += LEVEL_COLORS.previous;
                              } else {
                                buttonClass += LEVEL_COLORS.default;
                              }
                            }
                            
                            return (
                              <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                                <button
                                  onClick={() => handleLocalLevelChange(student.id, selectedSkillId, levelIndex)}
                                  className={buttonClass}
                                  title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}`}
                                >
                                  <span className="text-center leading-tight">{level}</span>
                                </button>
                              </td>
                            );
                          })}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
              
              {/* Controles de navegación rápida */}
              <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Volver arriba
                  </button>
                </div>
              </div>
            </div>
          </div>
      </div>
    );
    };
  }, [selectedSkillId, filteredStudents, showAllYears, handleLocalLevelChange, previouslySelectedLevels, getExpectedLevel, getPreviousYearLevel]);

  const renderSelectionButtons = () => {
    if (viewMode === 'student') {
    return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Estudiante:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar estudiante..." 
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="course">Curso</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {filteredStudents.length === 0 ? (
              <div className="col-span-full text-center py-4 text-gray-500">
                No se encontraron estudiantes con "{searchTerm}"
              </div>
            ) : (
              filteredStudents.map((student) => {
                const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
                // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                const progress = calculateFormProgress(
                  pensamientoMatematicosDimension.id,
                  student.course,
                  student.skillLevels
                );
                const percentage = progress.percentage;
                const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));
                
                return (
                <button
                  key={student.id}
                  onClick={(event) => handleStudentSelect(event, student.id as any)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, isSelected)
                    }`}
                  >
                    <div className="font-semibold" title={`${student.name} - ${student.course}`}>
                  {student.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, isSelected)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
                </button>
                );
              })
            )}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">≥75% completado,</span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">≥50% completado,</span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">≥25% completado,</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
              <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Ctrl + clic para seleccionar múltiples estudiantes
              </span>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
          <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Habilidad:</h3>
          
          <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
            {/* Barra de búsqueda */}
            <div className="relative w-full md:w-96 mx-auto">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                </svg>
              </div>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                placeholder="Buscar habilidad..." 
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>
            
            {/* Selector de ordenamiento */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="name">Nombre</option>
                <option value="lastName">Número</option>
                <option value="course">% Completado</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {pensamientoMatematicosDimension.skills
            .filter(skill => 
              searchTerm === '' || 
              skill.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => {
              if (sortBy === 'name') return a.name.localeCompare(b.name);
              if (sortBy === 'lastName') return a.id - b.id; // Por número
              if (sortBy === 'course') { // Por % completado
                const aCompleted = filteredStudents.filter(student => 
                  getSkillLevel(student, pensamientoMatematicosDimension.id, a.id) !== null
                ).length;
                const bCompleted = filteredStudents.filter(student => 
                  getSkillLevel(student, pensamientoMatematicosDimension.id, b.id) !== null
                ).length;
                const aPercentage = filteredStudents.length > 0 ? (aCompleted / filteredStudents.length) * 100 : 0;
                const bPercentage = filteredStudents.length > 0 ? (bCompleted / filteredStudents.length) * 100 : 0;
                return bPercentage - aPercentage;
              }
              return 0;
            })
            .map((skill) => {
              const completed = filteredStudents.filter(student => 
                getSkillLevel(student, pensamientoMatematicosDimension.id, skill.id) !== null
              ).length;
              const percentage = filteredStudents.length > 0 ? (completed / filteredStudents.length) * 100 : 0;
              
              return (
              <button
                key={skill.id}
                onClick={() => setSelectedSkillId(skill.id)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                    getCompletionColor(percentage, selectedSkillId === skill.id)
                  }`}
                >
                  <div className="font-semibold" title={skill.name}>
                {skill.name}
                  </div>
                  <div className={`text-xs font-medium ${getPercentageTextColor(percentage, selectedSkillId === skill.id)}`}>
                    {percentage.toFixed(1)}% completado
                  </div>
              </button>
              );
            })}
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center flex-wrap">
              <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥75% completado,</span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥50% completado,</span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥25% completado,</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">&lt;25% completado</span>
              </div>
            </div>
            <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
              Tip: Busque y ordene para encontrar habilidades específicas
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderDataInputView = useMemo(() => {
    return () => (
      <div>
        {viewMode === 'student' ? renderStudentView() : renderSkillView()}
      </div>
    );
  }, [viewMode, renderStudentView, renderSkillView]);



  return (
    <div className={DIMENSION_STYLES.container}>
      <UnifiedHeader
        dimensionName={pensamientoMatematicosDimension.name}
        icon={FaCalculator}
        skillsCount={pensamientoMatematicosDimension.skills.length}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />
        
        {/* Botones de modo de vista */}
        <div className="mt-3">
          <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-md">
            <div className="grid grid-cols-2 gap-1">
              <button
                onClick={() => handleViewModeChange('student')}
                className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                  viewMode === 'student' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FaUser className="mr-1.5 text-sm" />
                <span className="font-medium">Por Estudiante</span>
            </button>
              
            <button
                onClick={() => handleViewModeChange('skill')}
                className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                  viewMode === 'skill' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FaList className="mr-1.5 text-sm" />
                <span className="font-medium">Por Habilidad</span>
              </button>
          </div>
        </div>
      </div>
      
      {renderSelectionButtons()}
      
      <div className="bg-white p-4 rounded-lg shadow-md">
        {renderDataInputView()}
        
        {/* Leyenda de colores para los niveles de las tablas */}
        {(viewMode === 'student' && selectedStudentIds.length > 0) || (viewMode === 'skill' && selectedSkillId !== null) ? (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🎯</span>
              Leyenda de colores para niveles de desarrollo:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel seleccionado (evaluado)</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-400 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel esperado (edad correspondiente)</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 border-2 border-gray-400 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel del año anterior</span>
          </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-50 border border-blue-300 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel previamente seleccionado</span>
        </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-white border border-gray-200 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Sin seleccionar</span>
      </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 rounded mr-2 flex items-center justify-center">
                  <span className="text-xs text-gray-400">N/A</span>
                </div>
                <span className="text-xs text-gray-700">No aplica para este nivel</span>
              </div>
            </div>
            
            {/* Footer con botón "Volver arriba" */}
            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-4 py-2 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
                </svg>
                Volver arriba
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const calculatePercentageAchieved = (dimension: { skills: Skill[] }) => {
  const totalLevels = dimension.skills.reduce((sum, skill) => sum + skill.levels.length, 0);
  const achievedLevels = dimension.skills.reduce((sum, skill) => {
    return sum + (skill.selectedLevelIndex === null ? 0 : skill.selectedLevelIndex + 1);
  }, 0);
  return (achievedLevels / totalLevels) * 100;
};

// Usar funciones centralizadas

const calculateExpectedLevel = (age: number) => {
  return calculateExpectedPercentage(age);
};

const calculateLastYearPercentage = (age: number) => {
  return calculatePreviousYearPercentage(age);
};

export default PensamientoMatematico;